import React, { useState, useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  Grid,
  IconButton,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Tooltip,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import fittingsData from '../fittingsData';
import {
  getAvailableDiameters,
  findStraightCap,
  CIRCUIT_FITTING_CATEGORIES,
  isEndFittingCompatibleWithMainPipe,
  isDistributionEndFittingCompatibleWithSquareProfile as isEndFittingCompatibleWithSquareProfile,
  isDistributionCircuitFittingCompatibleWithSquareProfile,
  isDistributionCircuitFittingCompatibleWithRoundPipe as isCircuitFittingCompatible,
  THERMAL_POWER_INPUT_MIN_KW,
  getMaxThermalPowerKwCollectorCharacteristics,
  pickLargestCompatibleFittingIdInCategory,
} from './collectorUtils';
import {
  COLLECTOR_END_CONNECTION_CATEGORIES,
  COLLECTOR_END_CONNECTION_TYPE_AUTO_FALLBACK_ORDER,
  categoryLabelToConnectionType,
  filterCollectorEndConnectionSizes,
  CONNECTION_SIZE_CATALOG,
  pickOptimalConnectionSizeKey,
  velocityForConnectionSize,
  getFittingIdFromConnection,
  getTransitionPipeDiameter,
  getPipeSpacingBetweenSupplyReturnMm,
  getMaxEndConnectionFlowM3hDistributionCollectorSquareMaxProfile,
} from './endConnectionVelocitySizing';
import { getCapUpdatesForConnectionFitting } from './endConnectionsLogic';
import { getPipeDiameterForFitting } from './connectionLogic';
import {
  getDistributionCollectorFirstLastOffsetsMm,
  computeDistributionCircuitSpacingLayout,
} from './distributionCollectorOffsets';
import {
  DISTRIBUTION_COLLECTOR_CLIENT_PARAMS_TABLE,
  DISTRIBUTION_COLLECTOR_CLIENT_POWER_STEPS,
  isDistributionClientPresetSquareVelocityException,
  resolvePresetPressure,
} from './distributionCollectorClientParams';
import { computeDistributionClientOverTablePipeSpacingFromSeparatorGeometryMm } from './collectorSeparatorClientGeometry';
import {
  CLIENT_THERMAL_DELTA_TEMP_CHOICES,
  clientUsesPresetDeltaTemp,
  CLIENT_STANDARD_PRODUCTS_DELTA_TEMP_LABEL,
  getDistributionCollectorPresetReferenceDeltaTemp,
  normalizeClientThermalDeltaTemp,
} from './clientPresetReferenceDelta';
import {
  CLIENT_END_CONNECTION_CATEGORY_CHOICES,
  CLIENT_STANDARD_PRODUCTS_CONNECTION_LABEL,
  clientUsesPresetCollectorSize,
  clientUsesPresetConnectionType,
  clientUsesCatalogPresetConnection,
  getDistributionCollectorPresetEndConnectionCategory,
  getDistributionCollectorPresetEndConnectionSize,
} from './clientPresetConnection';
import {
  clientCatalogPresetKeyCeilByTableFlowM3h,
  resolveClientConstructionFromThermalInput,
} from './clientConstructionPreset';
import {
  getDistributionCollectorClientMaxTableFlowM3h,
  isClientFlowM3hOverCatalogTable,
} from './clientPresetCatalogFlow';
import { useAuth } from '../../contexts/AuthContext';

const COLLECTOR_CLIENT_POWER_STEPS = DISTRIBUTION_COLLECTOR_CLIENT_POWER_STEPS;
const COLLECTOR_CLIENT_POWER_STEPS_MAX =
  COLLECTOR_CLIENT_POWER_STEPS[COLLECTOR_CLIENT_POWER_STEPS.length - 1];
/** Запасной эталон ΔT, если в строке пресета не задан referenceDeltaTemp. */
const CLIENT_THERMAL_CALC_DELTA_FALLBACK_KW = 20;
/** Режим «для клиентов»: подбор размера торца источника ближе к этой скорости (как у гидроразделителя, м/с). */
const CLIENT_SOURCE_CONNECTION_TARGET_VELOCITY_MS = 1.0;
/** Целевая скорость в корпусе гребёнки при автоподборе ⌀/профиля (м/с). */
const COLLECTOR_BODY_TARGET_VELOCITY_MS = 0.6;
/** Режим «для инженеров»: подбор торца источника ближе к этой скорости (м/с). */
const COLLECTOR_ENGINEERS_SOURCE_CONNECTION_TARGET_VELOCITY_MS = 0.9;

/** Допустимые ΔT в режиме «для инженеров» (совпадает с пунктами в форме). */
const COLLECTOR_ENGINEERS_DELTA_TEMP_CHOICES = [5, 10, 15, 20, 25];
/** Значение ΔT по умолчанию для инженеров. */
const COLLECTOR_ENGINEERS_DEFAULT_DELTA_TEMP_KW = 25;

/** Компактный распределительный коллектор (квадрат): мощность, расход, общий размер (как в редакторе). */
const COMPACT_DISTRIBUTION_COLLECTOR_PRESETS = [
  { powerKw: 60, flowM3h: 3, compactSize: '60x60' },
  { powerKw: 120, flowM3h: 5, compactSize: '80x80' },
];
const COMPACT_DISTRIBUTION_SIZE_TO_PROFILES = {
  '60x60': { sheet: '60x30', profile: '60x30' },
  '80x80': { sheet: '80x40', profile: '80x40' },
};
const COMPACT_DISTRIBUTION_FIRST_LAST_OFFSET_MM = {
  '60x60': 30,
  '80x80': 53,
};

const COLLECTOR_CHARACTERISTICS_VIEW_MODE_KEY = 'collectorCharacteristicsViewMode';

function initialCollectorCalculatorViewMode(initialParams) {
  const vm = initialParams?._calculatorViewMode;
  if (vm === 'clients' || vm === 'engineers') return vm;
  try {
    const saved = localStorage.getItem(COLLECTOR_CHARACTERISTICS_VIEW_MODE_KEY);
    if (saved === 'clients' || saved === 'engineers') return saved;
  } catch (_) {}
  return 'clients';
}

function syncClientDistributionThermalFlowFromPower(next) {
  const p = Number(next.power);
  if (!Number.isFinite(p) || p <= 0) return;
  const q = dcCalcFlowM3hFromPowerKw(p, next.deltaTemp);
  if (!Number.isFinite(q) || q <= 0) return;
  next.flowRate = parseFloat(q.toFixed(2));
}

function resolveClientDistributionConstructionContext(state) {
  const maxTableQ = getDistributionCollectorClientMaxTableFlowM3h(state.shape);
  return resolveClientConstructionFromThermalInput({
    inputMode: state.inputMode,
    powerKw: state.power,
    flowRateM3h: state.flowRate,
    deltaTemp: state.deltaTemp,
    shape: state.shape,
    paramsTable: DISTRIBUTION_COLLECTOR_CLIENT_PARAMS_TABLE,
    powerSteps: COLLECTOR_CLIENT_POWER_STEPS,
    maxStepKw: COLLECTOR_CLIENT_POWER_STEPS_MAX,
    maxTableFlowM3h: maxTableQ,
    calcFlowFromPowerKw: (powerKw, dt) => dcCalcFlowM3hFromPowerKw(powerKw, dt),
  });
}

/** Ограничение диапазона кВт без снэпа на шаги каталога. */
function clampCollectorClientPowerKw(raw, maxP) {
  return Math.min(Math.max(raw, THERMAL_POWER_INPUT_MIN_KW), maxP);
}

const DC_WATER_DENSITY = 1000;
const DC_WATER_CP = 4190;

/** Ключ пресета распределительного коллектора (кВт) по расходу — таблица + ΔT (вода). */
function distributionCollectorClientPresetKeyFromFlow(flowNum, _deltaTemp, shape) {
  if (!Number.isFinite(flowNum) || flowNum <= 0) return null;
  const maxTableQ = getDistributionCollectorClientMaxTableFlowM3h(shape);
  if (isClientFlowM3hOverCatalogTable(flowNum, maxTableQ)) return null;
  return clientCatalogPresetKeyCeilByTableFlowM3h(
    flowNum,
    shape,
    DISTRIBUTION_COLLECTOR_CLIENT_PARAMS_TABLE,
    COLLECTOR_CLIENT_POWER_STEPS
  );
}

function dcCalcFlowM3hFromPowerKw(powerKw, deltaTemp) {
  const dt = normalizeClientThermalDeltaTemp(deltaTemp);
  if (!Number.isFinite(powerKw) || powerKw <= 0 || dt <= 0) return 0;
  return ((powerKw * 1000) / (DC_WATER_DENSITY * DC_WATER_CP * dt)) * 3600;
}

function resolveClientDistributionPresetKeyFromState(state) {
  const ctx = resolveClientDistributionConstructionContext(state);
  if (!ctx || ctx.overTable) return null;
  return ctx.constructionKeyKw;
}

function applyClientDistributionDeltaTempFromConstructionPreset(next) {
  if (!clientUsesPresetDeltaTemp(next)) return;
  const key = resolveClientDistributionPresetKeyFromState(next);
  if (key == null || !DISTRIBUTION_COLLECTOR_CLIENT_PARAMS_TABLE[key]) return;
  next.deltaTemp = getDistributionCollectorPresetReferenceDeltaTemp(key);
}

function seedClientDistributionConnectionFromCatalog(next) {
  const key = resolveClientDistributionPresetKeyFromState(next);
  if (key == null || !DISTRIBUTION_COLLECTOR_CLIENT_PARAMS_TABLE[key]) return;
  const categoryLabel = getDistributionCollectorPresetEndConnectionCategory(key);
  next.endConnectionCategory = categoryLabel;
  const sz = getDistributionCollectorPresetEndConnectionSize(key, categoryLabel);
  if (sz != null) next.endConnectionSize = sz;
}

function applyClientDistributionConnectionFromConstructionPreset(next) {
  if (!clientUsesPresetConnectionType(next)) return;
  seedClientDistributionConnectionFromCatalog(next);
}

function seedClientDistributionCollectorFromCatalog(next) {
  const key = resolveClientDistributionPresetKeyFromState(next);
  if (key == null || !DISTRIBUTION_COLLECTOR_CLIENT_PARAMS_TABLE[key]) return;
  const row = DISTRIBUTION_COLLECTOR_CLIENT_PARAMS_TABLE[key];
  if (next.shape === 'square') {
    if (row.squareProfile) next.collectorProfile = row.squareProfile;
  } else if (row.diameter != null) {
    next.collectorDiameter = row.diameter;
  }
}

function applyClientDistributionCollectorFromConstructionPreset(next) {
  if (!clientUsesPresetCollectorSize(next)) return;
  seedClientDistributionCollectorFromCatalog(next);
}

function applyClientDistributionPowerPresetSnap(next, clampedPowerKw) {
  if (!Number.isFinite(clampedPowerKw) || clampedPowerKw <= 0) {
    next.power = clampedPowerKw;
    return;
  }
  const qSnap = dcCalcFlowM3hFromPowerKw(clampedPowerKw, next.deltaTemp);
  const maxTableQ = getDistributionCollectorClientMaxTableFlowM3h(next.shape);
  if (isClientFlowM3hOverCatalogTable(qSnap, maxTableQ)) {
    next.power = clampedPowerKw;
    next.flowRate = parseFloat(qSnap.toFixed(2));
    return;
  }
  next.power = clampCollectorClientPowerKw(clampedPowerKw, COLLECTOR_CLIENT_POWER_STEPS_MAX);
  syncClientDistributionThermalFlowFromPower(next);
  applyClientDistributionDeltaTempFromConstructionPreset(next);
  applyClientDistributionConnectionFromConstructionPreset(next);
  applyClientDistributionCollectorFromConstructionPreset(next);
}

/**
 * Старт без сохранённых настроек: сразу ⌀/расход/торец из пресета (круг), иначе первый кадр
 * берёт optimalDiameter ≈ 48.3 и поджимает ниппели контуров до ¾".
 */
const DISTRIBUTION_COLLECTOR_DEFAULT_START_POWER_KW = 85;

function getDefaultClientRoundPresetFields() {
  const row = DISTRIBUTION_COLLECTOR_CLIENT_PARAMS_TABLE[DISTRIBUTION_COLLECTOR_DEFAULT_START_POWER_KW];
  if (!row) {
    return {
      power: DISTRIBUTION_COLLECTOR_DEFAULT_START_POWER_KW,
      flowRate: 2.92,
      collectorDiameter: null,
      endConnectionSize: null,
      pressure: 6,
    };
  }
  const connSize =
    row.nipple && row.flange ? row.nipple.connectionSize : row.connectionSize ?? null;
  return {
    power: DISTRIBUTION_COLLECTOR_DEFAULT_START_POWER_KW,
    flowRate: row.flowRate,
    collectorDiameter: row.diameter ?? null,
    endConnectionSize: connSize,
    pressure: resolvePresetPressure(row, false),
  };
}

// ─── Хелперы для контуров ──────────────────────────────────────────────────
const DEFAULT_FITTING_CATEGORY = 'Ниппели';
const DEFAULT_FITTING_ID       = 'Nippel_1';

/**
 * Короткое обозначение размера фитинга для компактного чипа.
 * Из «Ниппель 1 1/2"» → «1½"»; из «Фланец Ду50 (...)» → «Ду50».
 */
const shortFittingLabel = (displayName) => {
  if (!displayName) return '—';
  // Ду-размер
  const du = displayName.match(/Ду\s*(\d+)/);
  if (du) return `Ду${du[1]}`;
  // Дюймовый размер: берём всё после первого слова (название категории)
  // «Ниппель 1 1/4"» → «1 1/4"» → заменяем дроби на Unicode
  const inch = displayName.match(/^[А-Яа-яA-Za-z\s]+?(\d[\d\s/]+")/);
  if (inch) {
    return inch[1]
      .trim()
      .replace('1/4', '¼')
      .replace('1/2', '½')
      .replace('3/4', '¾')
      .replace('3/8', '⅜');
  }
  // Fallback — всё после первого пробела
  const parts = displayName.split(' ');
  return parts.slice(1).join(' ') || displayName;
};

const getFittingsByCategory = (category, compatibilityFn = null) =>
  Object.entries(fittingsData)
    .filter(([, d]) => d.category === category)
    .map(([id, d]) => ({
      id,
      label: d.displayName,
      shortLabel: shortFittingLabel(d.displayName),
      diameter: d.diameter,
      isCompatible: compatibilityFn ? compatibilityFn(id) : true,
    }))
    .sort((a, b) => a.diameter - b.diameter);

/** Несовместимые фитинги → максимально крупный допустимый в той же категории (как при смене ⌀ в редакторе). */
const clampCircuitsToCompatibility = (circuits, isCompatible) =>
  circuits.map((c) => {
    if (!c.fitting || isCompatible(c.fitting)) return c;
    const cat = c.fittingCategory ?? DEFAULT_FITTING_CATEGORY;
    const best = pickLargestCompatibleFittingIdInCategory(cat, isCompatible);
    const nextId = best ?? c.fitting;
    return { ...c, fitting: nextId };
  });

/** Торцевые контуры: правила isEndFitting*; подбор только из категории торцевого подключения в форме (как supply*ConnectionFittingCategory в редакторе). */
const clampEndCircuitToCompatibility = (end, isCompatible, endConnectionCategory) => {
  if (!end?.fitting) return end;
  const cat = endConnectionCategory ?? end.fittingCategory ?? DEFAULT_FITTING_CATEGORY;
  const catalogCategory = fittingsData[end.fitting]?.category;
  const categoryMismatch =
    catalogCategory != null && catalogCategory !== cat;
  if (isCompatible(end.fitting) && !categoryMismatch) return end;
  const best = pickLargestCompatibleFittingIdInCategory(cat, isCompatible);
  const nextId = best ?? end.fitting;
  return { ...end, fitting: nextId, fittingCategory: cat };
};

/** Видимость слота «торцевой контур» в UI (свободный торец относительно подключения источника). */
const endContourSlotVisibleLeft = (connectionSide) =>
  connectionSide !== 'left' && connectionSide !== 'both';
const endContourSlotVisibleRight = (connectionSide) =>
  connectionSide !== 'right' && connectionSide !== 'both';

/**
 * При смене стороны подключения источника: переносим фитинг со старого свободного торца на новый,
 * очищаем скрытые слоты (иначе в расчёт попадали бы два торцевых подключения).
 */
const reconcileEndCircuitsOnConnectionSideChange = (prevSide, nextSide, left, right) => {
  const wasL = endContourSlotVisibleLeft(prevSide);
  const wasR = endContourSlotVisibleRight(prevSide);
  const nowL = endContourSlotVisibleLeft(nextSide);
  const nowR = endContourSlotVisibleRight(nextSide);

  if (wasR && !wasL && nowL && !nowR) {
    return { newLeft: right, newRight: null };
  }
  if (wasL && !wasR && nowR && !nowL) {
    return { newLeft: null, newRight: left };
  }

  if (nextSide === 'both') {
    return { newLeft: null, newRight: null };
  }

  return {
    newLeft: nowL ? left : null,
    newRight: nowR ? right : null,
  };
};

let circuitIdCounter = 0;
const newCircuitId = () => `c-${++circuitIdCounter}`;

/** Подпись поля «Заглушка» */
function capDisplayLabel(shape, pressure, roundOuterMm) {
  if (shape === 'square') return 'Прямая (4/6 бар)';
  // Прямые заглушки по правилам изделия — только до ⌀ 60.3 включительно
  if (roundOuterMm != null && roundOuterMm > 60.3) {
    return 'Эллиптическая (8/10 бар)';
  }
  if (roundOuterMm != null && !findStraightCap(roundOuterMm)) {
    return 'Эллиптическая (8/10 бар)';
  }
  return Number(pressure) >= 8 ? 'Эллиптическая (8/10 бар)' : 'Прямая (6/8 бар)';
}

const COLLECTOR_CHARACTERISTICS_SQUARE_PROFILES = [
  { value: '40x40', label: '40×40', side: 40 },
  { value: '50x50', label: '50×50', side: 50 },
  { value: '60x60', label: '60×60', side: 60 },
  { value: '80x80', label: '80×80', side: 80 },
  { value: '100x100', label: '100×100', side: 100 },
  { value: '150x150', label: '150×150', side: 150 },
];

const COLLECTOR_CHARACTERISTICS_SQUARE_WALL_MM = { 40: 2, 50: 2, 60: 2, 80: 2, 100: 2, 150: 3 };

function collectorCharacteristicsSquareWallThickness(side) {
  return COLLECTOR_CHARACTERISTICS_SQUARE_WALL_MM[side] ?? 2;
}

const COLLECTOR_CHARACTERISTICS_ROUND_WALL_MM = {
  42.4: 2, 48.3: 2, 60.3: 2, 76.1: 2, 88.9: 2,
  101.6: 2, 114.3: 2, 129: 2, 139.7: 2, 159: 2,
  168.3: 3, 219.1: 3, 273: 3, 323.9: 3, 355.6: 3,
  406.4: 3, 508: 3, 609.6: 3,
};

function collectorCharacteristicsRoundWallThickness(outerDiameter) {
  return COLLECTOR_CHARACTERISTICS_ROUND_WALL_MM[outerDiameter] ?? 2;
}

/**
 * Расчёт по характеристикам для распределительного коллектора.
 * Режим «Для клиентов» / «Для инженеров» (переключатель у администратора), как у гидроразделителя.
 *
 * Пользователь вводит:
 *  - сечение (круглое / квадратное)
 *  - тепловую мощность или объёмный расход
 *  - разницу температур ΔT
 *  - давление (как для гидроразделителя: влияет на прямую / эллиптическую заглушку у круга)
 *  - категорию и размер торцевого фитинга (скорость в подключении 0.3–1.5 м/с)
 *  - сторону торцевого подключения (слева / справа / оба / нет)
 *
 * Пересчёт мощность ↔ расход — для воды (ρ = 1000 кг/м³, c = 4190 Дж/(кг·°C)).
 * Компонент формирует объект params, совместимый с DistributionCollectorEditor.
 */
function CollectorCharacteristicsCalculator({ onBack, onCalculate, onBackToDrawing, initialParams }) {
  const { isAdmin } = useAuth();
  const [viewMode, setViewMode] = useState(() => initialCollectorCalculatorViewMode(initialParams));
  const lastInitialParamsRef = useRef(null);
  const shouldSaveToLocalStorage = useRef(true);
  const prevClientPresetPowerKeyRef = useRef(null);
  const prevClientPresetShapeRef = useRef(null);
  /** Последняя ΔT в режиме инженера — чтобы не терять её при фиксации 20 °C у клиентов. */
  const preservedEngineersDeltaTempRef = useRef(null);

  // ─── Круглые диаметры из каталога (> 38, без 54 и 306) ─────────────────────
  const availableDiameters = useMemo(() => getAvailableDiameters(), []);

  const availableSquareProfiles = COLLECTOR_CHARACTERISTICS_SQUARE_PROFILES;

  // ─── Состояние ─────────────────────────────────────────────────────────────
  const cleanupSaved = (obj) => {
    const c = { ...obj };
    delete c.showCompactDistributionOptions;
    delete c.fluid;
    delete c.connectionPipeDiameter;
    delete c.circuitSpacing;
    if (!c.endConnectionCategory) {
      c.endConnectionCategory = 'Ниппели';
    }
    if (c.pressure == null || c.pressure === '') {
      c.pressure = c.shape === 'square' ? 4 : 6;
    } else {
      const p = Number(c.pressure);
      c.pressure = Number.isNaN(p) ? (c.shape === 'square' ? 4 : 6) : p;
    }
    if (c.useCompactDistribution == null) c.useCompactDistribution = false;
    if (c.compactDistributionPresetKw == null) c.compactDistributionPresetKw = 60;
    if (c.clientPresetDeltaTemp === undefined) {
      c.clientPresetDeltaTemp = !Boolean(c.clientManualDeltaTemp);
    } else {
      c.clientPresetDeltaTemp = Boolean(c.clientPresetDeltaTemp);
    }
    if (c.clientPresetConnectionType === undefined) {
      c.clientPresetConnectionType = true;
    } else {
      c.clientPresetConnectionType = Boolean(c.clientPresetConnectionType);
    }
    if (c.clientPresetCollectorSize === undefined) {
      c.clientPresetCollectorSize = true;
    } else {
      c.clientPresetCollectorSize = Boolean(c.clientPresetCollectorSize);
    }
    return c;
  };

  const [characteristics, setCharacteristics] = useState(() => {
    if (initialParams && initialParams._characteristics) {
      lastInitialParamsRef.current = initialParams;
      shouldSaveToLocalStorage.current = false;
      return cleanupSaved(initialParams._characteristics);
    }
    try {
      const saved = localStorage.getItem('collectorCharacteristics');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.inputMode && parsed.deltaTemp) return cleanupSaved(parsed);
      }
    } catch (e) {
      console.error('Ошибка при восстановлении collectorCharacteristics:', e);
    }
    const d0 = getDefaultClientRoundPresetFields();
    return {
      inputMode: 'power',
      power: d0.power,
      flowRate: d0.flowRate,
      deltaTemp: getDistributionCollectorPresetReferenceDeltaTemp(d0.power),
      clientPresetDeltaTemp: true,
      clientPresetConnectionType: true,
      clientPresetCollectorSize: true,
      shape: 'round',            // 'round' | 'square'
      collectorDiameter: d0.collectorDiameter,
      collectorProfile: null,    // квадратное: null = авто, иначе строка '80x80'
      endConnectionCategory: 'Ниппели',
      endConnectionSize: d0.endConnectionSize,
      connectionSide: 'left',
      circuitCount: 3,
      pressure: d0.pressure,
      useCompactDistribution: false,
      compactDistributionPresetKw: 60,
    };
  });

  /** Зафиксированные мощность/расход: подбор сечения и поджим контуров гребёнки только после blur (не при каждом вводе). */
  const [committedThermal, setCommittedThermal] = useState(null);

  useLayoutEffect(() => {
    setCommittedThermal((prev) =>
      prev != null ? prev : { power: characteristics.power, flowRate: characteristics.flowRate }
    );
    // только первый синхрон: избежать первого кадра с committed === null → «живая» мощность для контуров
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Контуры (подача и обратка) ─────────────────────────────────────────────
  const makeDefaultCircuit = (saved) => ({
    id: newCircuitId(),
    fittingCategory: saved?.fittingCategory ?? DEFAULT_FITTING_CATEGORY,
    fitting:         saved?.fitting         ?? DEFAULT_FITTING_ID,
  });

  const initCircuits = (savedList, count) => {
    if (savedList && savedList.length > 0) return savedList.map(makeDefaultCircuit);
    return Array.from({ length: count ?? 3 }, () => makeDefaultCircuit());
  };

  const [supplyCircuits, setSupplyCircuits] = useState(() =>
    initCircuits(initialParams?._characteristics?.supplyCircuits, initialParams?._characteristics?.circuitCount)
  );
  const [returnCircuits, setReturnCircuits] = useState(() =>
    initCircuits(initialParams?._characteristics?.returnCircuits, initialParams?._characteristics?.circuitCount)
  );

  // Торцевые контуры: null = нет, объект = есть фитинг
  const makeDefaultEndCircuit = (saved) => saved
    ? { fittingCategory: saved.fittingCategory ?? DEFAULT_FITTING_CATEGORY, fitting: saved.fitting ?? DEFAULT_FITTING_ID }
    : null;

  const [leftEndCircuit,  setLeftEndCircuit]  = useState(() =>
    makeDefaultEndCircuit(initialParams?._characteristics?.leftEndCircuit)
  );
  const [rightEndCircuit, setRightEndCircuit] = useState(() =>
    makeDefaultEndCircuit(initialParams?._characteristics?.rightEndCircuit)
  );

  const leftEndCircuitRef = useRef(leftEndCircuit);
  const rightEndCircuitRef = useRef(rightEndCircuit);
  leftEndCircuitRef.current = leftEndCircuit;
  rightEndCircuitRef.current = rightEndCircuit;
  const prevConnectionSideForEndRef = useRef(characteristics.connectionSide);

  useEffect(() => {
    const next = characteristics.connectionSide;
    const prev = prevConnectionSideForEndRef.current;
    if (prev === next) return;
    prevConnectionSideForEndRef.current = next;

    const left = leftEndCircuitRef.current;
    const right = rightEndCircuitRef.current;
    const { newLeft, newRight } = reconcileEndCircuitsOnConnectionSideChange(prev, next, left, right);

    const sameLeft =
      (newLeft == null && left == null) ||
      (newLeft != null && left != null && newLeft.fitting === left.fitting && newLeft.fittingCategory === left.fittingCategory);
    const sameRight =
      (newRight == null && right == null) ||
      (newRight != null && right != null && newRight.fitting === right.fitting && newRight.fittingCategory === right.fittingCategory);
    if (!sameLeft) setLeftEndCircuit(newLeft);
    if (!sameRight) setRightEndCircuit(newRight);
  }, [characteristics.connectionSide]);

  useEffect(() => {
    if (viewMode !== 'engineers') return;
    const saved = preservedEngineersDeltaTempRef.current;
    const nextDt =
      saved != null && COLLECTOR_ENGINEERS_DELTA_TEMP_CHOICES.includes(saved)
        ? saved
        : COLLECTOR_ENGINEERS_DEFAULT_DELTA_TEMP_KW;
    setCharacteristics((prev) => {
      if (Number(prev.deltaTemp) === nextDt) return prev;
      return { ...prev, deltaTemp: nextDt };
    });
  }, [viewMode]);

  useEffect(() => {
    try {
      localStorage.setItem(COLLECTOR_CHARACTERISTICS_VIEW_MODE_KEY, viewMode);
    } catch (_) {}
  }, [viewMode]);

  // Popover: выбор категории → фитинга
  // row: 'supply' | 'return' | 'leftEnd' | 'rightEnd'
  const [circuitPopover, setCircuitPopover] = useState(null);

  const openCircuitPopover = (e, row, index = null) => {
    // Захватываем координаты сразу — до любых setState и ре-рендеров
    const el = e?.currentTarget ?? e?.target;
    const rect = el?.getBoundingClientRect();
    const anchorPosition = rect
      ? { top: rect.bottom + window.scrollY, left: rect.left + window.scrollX }
      : null;
    let currentFittingCategory = DEFAULT_FITTING_CATEGORY;
    if (row === 'supply') currentFittingCategory = supplyCircuits[index]?.fittingCategory ?? DEFAULT_FITTING_CATEGORY;
    else if (row === 'return') currentFittingCategory = returnCircuits[index]?.fittingCategory ?? DEFAULT_FITTING_CATEGORY;
    else if (row === 'leftEnd') {
      currentFittingCategory = characteristics.endConnectionCategory
        ?? leftEndCircuit?.fittingCategory
        ?? DEFAULT_FITTING_CATEGORY;
    } else if (row === 'rightEnd') {
      currentFittingCategory = characteristics.endConnectionCategory
        ?? rightEndCircuit?.fittingCategory
        ?? DEFAULT_FITTING_CATEGORY;
    }
    setCircuitPopover({ anchorPosition, row, index, step: 'category', category: currentFittingCategory });
  };
  const closeCircuitPopover = () => setCircuitPopover(null);

  const selectCategory = (category) => {
    setCircuitPopover(prev => ({ ...prev, step: 'fitting', category }));
  };

  const selectFitting = (fittingId) => {
    if (!circuitPopover) return;
    const { row, index, category } = circuitPopover;
    if (row === 'supply') {
      setSupplyCircuits(prev => prev.map((c, i) => i === index ? { ...c, fittingCategory: category, fitting: fittingId } : c));
    } else if (row === 'return') {
      setReturnCircuits(prev => prev.map((c, i) => i === index ? { ...c, fittingCategory: category, fitting: fittingId } : c));
    } else if (row === 'leftEnd') {
      setLeftEndCircuit({ fittingCategory: category, fitting: fittingId });
    } else if (row === 'rightEnd') {
      setRightEndCircuit({ fittingCategory: category, fitting: fittingId });
    }
    closeCircuitPopover();
  };

  const addCircuit = (row) => {
    const newC = makeDefaultCircuit();
    if (row === 'supply') setSupplyCircuits(prev => [...prev, newC]);
    else setReturnCircuits(prev => [...prev, newC]);
  };

  const removeCircuit = (row, index) => {
    const upd = (prev) => prev.filter((_, i) => i !== index);
    if (row === 'supply') setSupplyCircuits(upd);
    else setReturnCircuits(upd);
  };

  // Синхронизируем при возврате из редактора
  useEffect(() => {
      if (initialParams && initialParams._characteristics) {
      const vm = initialParams._calculatorViewMode;
      if (vm === 'clients' || vm === 'engineers') setViewMode(vm);
      const prev = lastInitialParamsRef.current?._characteristics;
      const next = initialParams._characteristics;
      if (!prev || JSON.stringify(prev) !== JSON.stringify(next)) {
        lastInitialParamsRef.current = initialParams;
        const ndt = Number(next.deltaTemp);
        if (vm === 'engineers' && COLLECTOR_ENGINEERS_DELTA_TEMP_CHOICES.includes(ndt)) {
          preservedEngineersDeltaTempRef.current = ndt;
        }
        const cleaned = cleanupSaved(next);
        setCharacteristics(cleaned);
        setCommittedThermal({ power: cleaned.power, flowRate: cleaned.flowRate });
        if (next.supplyCircuits) setSupplyCircuits(next.supplyCircuits.map(c => ({ ...c, id: newCircuitId() })));
        if (next.returnCircuits) setReturnCircuits(next.returnCircuits.map(c => ({ ...c, id: newCircuitId() })));
        setLeftEndCircuit(makeDefaultEndCircuit(next.leftEndCircuit));
        setRightEndCircuit(makeDefaultEndCircuit(next.rightEndCircuit));
      }
    } else if (!initialParams) {
      lastInitialParamsRef.current = null;
    }
  }, [initialParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // После blur к Вт/м³·ч совпадает с формой; при смене режима/сечения — сбрасываем к актуальной форме.
  /* power/flow в deps не добавляем — иначе committed сбросится при каждом вводе и контуры будут дергаться. */
  useEffect(() => {
    setCommittedThermal({
      power: characteristics.power,
      flowRate: characteristics.flowRate,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- только смена режима/сечения (см. блок выше)
  }, [
    characteristics.shape,
    characteristics.deltaTemp,
    viewMode,
    characteristics.inputMode,
    characteristics.useCompactDistribution,
    characteristics.compactDistributionPresetKw,
  ]);

  // Сохраняем в localStorage
  useEffect(() => {
    if (!shouldSaveToLocalStorage.current) {
      shouldSaveToLocalStorage.current = true;
      return;
    }
    try {
      localStorage.setItem('collectorCharacteristics', JSON.stringify(characteristics));
    } catch (e) {
      console.error('Ошибка при сохранении collectorCharacteristics:', e);
    }
  }, [characteristics]);

  // ─── Физика ─────────────────────────────────────────────────────────────────
  const WATER_DENSITY = 1000;
  const WATER_SPECIFIC_HEAT = 4190;

  const calculateFlowRateFromPower = (power, deltaTemp) => {
    if (!power || deltaTemp === 0) return 0;
    return ((power * 1000) / (WATER_DENSITY * WATER_SPECIFIC_HEAT * deltaTemp)) * 3600;
  };

  const calculatePowerFromFlowRate = (flowRate, deltaTemp) => {
    if (!flowRate || deltaTemp === 0) return 0;
    return ((flowRate / 3600) * WATER_DENSITY * WATER_SPECIFIC_HEAT * deltaTemp) / 1000;
  };

  const calculateVelocityRound = (flowRateM3h, outerDiameter, wallThickness) => {
    if (!flowRateM3h || !outerDiameter) return 0;
    const inner = outerDiameter - 2 * wallThickness;
    const area = Math.PI * Math.pow(inner / 1000 / 2, 2);
    return (flowRateM3h / 3600) / area;
  };

  const calculateVelocitySquare = (flowRateM3h, side, wallThickness) => {
    if (!flowRateM3h || !side) return 0;
    const inner = side - 2 * wallThickness;
    const area = (inner / 1000) * (inner / 1000);
    return (flowRateM3h / 3600) / area;
  };

  /** ΔT для теплогидравлики (клиенты и инженеры — из формы). */
  const effectiveThermalDeltaTemp = useMemo(() => {
    return normalizeClientThermalDeltaTemp(characteristics.deltaTemp);
  }, [characteristics.deltaTemp]);

  const clientPresetDeltaTempOn = clientUsesPresetDeltaTemp({
    clientPresetDeltaTemp: characteristics.clientPresetDeltaTemp,
    clientManualDeltaTemp: characteristics.clientManualDeltaTemp,
  });

  const clientPresetConnectionTypeOn = clientUsesPresetConnectionType({
    clientPresetConnectionType: characteristics.clientPresetConnectionType,
  });

  const clientPresetCollectorSizeOn = clientUsesPresetCollectorSize({
    clientPresetCollectorSize: characteristics.clientPresetCollectorSize,
  });

  const charForCircuitSizing = useMemo(() => {
    if (committedThermal == null) return characteristics;
    return {
      ...characteristics,
      power:
        characteristics.inputMode === 'power' && committedThermal.power !== undefined
          ? committedThermal.power
          : characteristics.power,
      flowRate:
        characteristics.inputMode === 'flowRate' && committedThermal.flowRate !== undefined
          ? committedThermal.flowRate
          : characteristics.flowRate,
    };
  }, [characteristics, committedThermal]);

  const clientPresetPowerKeyForCircuits = useMemo(() => {
    if (viewMode !== 'clients' || charForCircuitSizing.useCompactDistribution) return null;
    const ctx = resolveClientDistributionConstructionContext(charForCircuitSizing);
    if (!ctx || ctx.overTable) return null;
    return ctx.constructionKeyKw;
  }, [viewMode, charForCircuitSizing]);

  const clientSizingLockedByPresetForCircuits =
    viewMode === 'clients' &&
    !charForCircuitSizing.useCompactDistribution &&
    clientPresetPowerKeyForCircuits != null &&
    DISTRIBUTION_COLLECTOR_CLIENT_PARAMS_TABLE[clientPresetPowerKeyForCircuits] &&
    clientUsesPresetCollectorSize(charForCircuitSizing);

  const {
    useCompactDistribution,
    inputMode: clientInputMode,
    power: clientPower,
    flowRate: clientFlowRate,
    deltaTemp: clientDeltaTemp,
    shape: clientShape,
  } = characteristics;

  const clientConstructionCtx = useMemo(() => {
    if (viewMode !== 'clients' || useCompactDistribution) return null;
    return resolveClientDistributionConstructionContext({
      inputMode: clientInputMode,
      power: clientPower,
      flowRate: clientFlowRate,
      deltaTemp: clientDeltaTemp,
      shape: clientShape,
    });
  }, [
    viewMode,
    useCompactDistribution,
    clientInputMode,
    clientPower,
    clientFlowRate,
    clientDeltaTemp,
    clientShape,
  ]);

  const clientPresetPowerKey =
    clientConstructionCtx && !clientConstructionCtx.overTable
      ? clientConstructionCtx.constructionKeyKw
      : null;

  const clientCatalogConnectionPresetOn =
    viewMode === 'clients' &&
    clientUsesCatalogPresetConnection(characteristics, clientConstructionCtx);

  const endConnectionCategoryMenuItems = useMemo(() => {
    if (clientCatalogConnectionPresetOn) {
      return CLIENT_END_CONNECTION_CATEGORY_CHOICES;
    }
    return COLLECTOR_END_CONNECTION_CATEGORIES.map(({ id }) => id);
  }, [clientCatalogConnectionPresetOn]);

  const clientConstructionSizingFlowM3h = clientConstructionCtx
    ? clientConstructionCtx.sizingFlowM3h
    : null;

  const clientSizingLockedByPreset =
    viewMode === 'clients' &&
    !characteristics.useCompactDistribution &&
    clientPresetPowerKey != null &&
    DISTRIBUTION_COLLECTOR_CLIENT_PARAMS_TABLE[clientPresetPowerKey] &&
    clientPresetCollectorSizeOn;

  const maxClientTableFlowM3h = useMemo(
    () => getDistributionCollectorClientMaxTableFlowM3h(characteristics.shape),
    [characteristics.shape]
  );

  const clientThermalFlowM3h = useMemo(() => {
    if (viewMode !== 'clients') return null;
    if (characteristics.inputMode === 'power') {
      const p =
        typeof characteristics.power === 'number' && !Number.isNaN(characteristics.power)
          ? characteristics.power
          : Number(characteristics.power);
      if (!Number.isFinite(p) || p <= 0) return null;
      return dcCalcFlowM3hFromPowerKw(p, effectiveThermalDeltaTemp);
    }
    const qn = Number(characteristics.flowRate);
    return Number.isFinite(qn) && qn > 0 ? qn : null;
  }, [
    viewMode,
    characteristics.inputMode,
    characteristics.power,
    characteristics.flowRate,
    effectiveThermalDeltaTemp,
  ]);

  /** Клиенты, расход выше табличного каталога: только фланец на подводе. */
  const clientFlangeOnlySourceConnection = useMemo(() => {
    if (viewMode !== 'clients' || clientThermalFlowM3h == null) return false;
    return isClientFlowM3hOverCatalogTable(clientThermalFlowM3h, maxClientTableFlowM3h);
  }, [viewMode, clientThermalFlowM3h, maxClientTableFlowM3h]);

  // ─── Актуальный расход ──────────────────────────────────────────────────────
  const actualFlowRate = useMemo(() => {
    const { inputMode, power, flowRate, shape, useCompactDistribution, compactDistributionPresetKw } =
      characteristics;
    if (shape === 'square' && useCompactDistribution) {
      const row = COMPACT_DISTRIBUTION_COLLECTOR_PRESETS.find((p) => p.powerKw === compactDistributionPresetKw);
      if (row) return row.flowM3h;
    }
    if (
      viewMode === 'clients' &&
      !clientFlangeOnlySourceConnection &&
      clientConstructionSizingFlowM3h != null &&
      Number.isFinite(clientConstructionSizingFlowM3h)
    ) {
      return clientConstructionSizingFlowM3h;
    }
    if (inputMode !== 'power') {
      if (
        viewMode === 'clients' &&
        Number.isFinite(flowRate) &&
        isClientFlowM3hOverCatalogTable(flowRate, getDistributionCollectorClientMaxTableFlowM3h(shape))
      ) {
        return flowRate;
      }
      return flowRate || 0;
    }
    if (power === '' || power === null || power === undefined) {
      return calculateFlowRateFromPower(0, effectiveThermalDeltaTemp);
    }
    const pn = typeof power === 'number' && !Number.isNaN(power) ? power : 0;
    if (!pn) return calculateFlowRateFromPower(0, effectiveThermalDeltaTemp);
    return calculateFlowRateFromPower(pn, effectiveThermalDeltaTemp);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    characteristics.inputMode,
    characteristics.power,
    characteristics.flowRate,
    effectiveThermalDeltaTemp,
    viewMode,
    clientPresetPowerKey,
    clientConstructionSizingFlowM3h,
    clientFlangeOnlySourceConnection,
    characteristics.shape,
    characteristics.useCompactDistribution,
    characteristics.compactDistributionPresetKw,
  ]);

  const actualFlowRateForCircuits = useMemo(() => {
    const { inputMode, power, flowRate, shape, useCompactDistribution, compactDistributionPresetKw } =
      charForCircuitSizing;
    if (shape === 'square' && useCompactDistribution) {
      const row = COMPACT_DISTRIBUTION_COLLECTOR_PRESETS.find((p) => p.powerKw === compactDistributionPresetKw);
      if (row) return row.flowM3h;
    }
    const circuitsCtx = resolveClientDistributionConstructionContext(charForCircuitSizing);
    const circuitsSizingFlow =
      viewMode === 'clients' &&
      circuitsCtx &&
      !circuitsCtx.overTable &&
      circuitsCtx.sizingFlowM3h != null
        ? circuitsCtx.sizingFlowM3h
        : null;
    if (
      viewMode === 'clients' &&
      circuitsSizingFlow != null &&
      Number.isFinite(circuitsSizingFlow)
    ) {
      return circuitsSizingFlow;
    }
    if (inputMode !== 'power') {
      if (
        viewMode === 'clients' &&
        Number.isFinite(flowRate) &&
        isClientFlowM3hOverCatalogTable(flowRate, getDistributionCollectorClientMaxTableFlowM3h(shape))
      ) {
        return flowRate;
      }
      return flowRate || 0;
    }
    if (power === '' || power === null || power === undefined) {
      return calculateFlowRateFromPower(0, effectiveThermalDeltaTemp);
    }
    const pn = typeof power === 'number' && !Number.isNaN(power) ? power : 0;
    if (!pn) return calculateFlowRateFromPower(0, effectiveThermalDeltaTemp);
    return calculateFlowRateFromPower(pn, effectiveThermalDeltaTemp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    charForCircuitSizing,
    effectiveThermalDeltaTemp,
    viewMode,
    clientPresetPowerKeyForCircuits,
  ]);

  const isSquare = characteristics.shape === 'square';

  const compactPresetRow = useMemo(() => {
    if (!isSquare || !characteristics.useCompactDistribution) return null;
    const kw = characteristics.compactDistributionPresetKw;
    return (
      COMPACT_DISTRIBUTION_COLLECTOR_PRESETS.find((p) => p.powerKw === kw) ??
      COMPACT_DISTRIBUTION_COLLECTOR_PRESETS[0]
    );
  }, [isSquare, characteristics.useCompactDistribution, characteristics.compactDistributionPresetKw]);

  useEffect(() => {
    if (!isSquare || !characteristics.useCompactDistribution) return;
    setLeftEndCircuit(null);
    setRightEndCircuit(null);
    setCircuitPopover((pop) =>
      pop && (pop.row === 'leftEnd' || pop.row === 'rightEnd') ? null : pop
    );
  }, [isSquare, characteristics.useCompactDistribution]);

  const COLLECTOR_MIN_VELOCITY = 0.2;
  const COLLECTOR_MAX_VELOCITY = 0.8;

  const sourceConnectionTargetVelocityMs = useMemo(() => {
    if (viewMode === 'clients') return CLIENT_SOURCE_CONNECTION_TARGET_VELOCITY_MS;
    return COLLECTOR_ENGINEERS_SOURCE_CONNECTION_TARGET_VELOCITY_MS;
  }, [viewMode]);

  // ─── Фильтрация круглых диаметров ──────────────────────────────────────────
  const filteredDiameters = useMemo(() => {
    if (clientSizingLockedByPreset) return availableDiameters;
    return availableDiameters.filter(({ value }) => {
      const wt = collectorCharacteristicsRoundWallThickness(value);
      const v = calculateVelocityRound(actualFlowRate, value, wt);
      return v >= COLLECTOR_MIN_VELOCITY && v <= COLLECTOR_MAX_VELOCITY;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualFlowRate, availableDiameters, clientSizingLockedByPreset]);

  // ─── Фильтрация квадратных профилей ────────────────────────────────────────
  const filteredProfiles = useMemo(() => {
    if (clientSizingLockedByPreset) return availableSquareProfiles;
    return availableSquareProfiles.filter(({ side, value }) => {
      if (isDistributionClientPresetSquareVelocityException(clientPresetPowerKey, value)) {
        return true;
      }
      const wt = collectorCharacteristicsSquareWallThickness(side);
      const v = calculateVelocitySquare(actualFlowRate, side, wt);
      return v >= COLLECTOR_MIN_VELOCITY && v <= COLLECTOR_MAX_VELOCITY;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualFlowRate, clientSizingLockedByPreset, clientPresetPowerKey]);

  const filteredDiametersForCircuits = useMemo(() => {
    if (clientSizingLockedByPresetForCircuits) return availableDiameters;
    return availableDiameters.filter(({ value }) => {
      const wt = collectorCharacteristicsRoundWallThickness(value);
      const v = calculateVelocityRound(actualFlowRateForCircuits, value, wt);
      return v >= COLLECTOR_MIN_VELOCITY && v <= COLLECTOR_MAX_VELOCITY;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualFlowRateForCircuits, availableDiameters, clientSizingLockedByPresetForCircuits]);

  const filteredProfilesForCircuits = useMemo(() => {
    if (clientSizingLockedByPresetForCircuits) return availableSquareProfiles;
    return availableSquareProfiles.filter(({ side, value }) => {
      if (isDistributionClientPresetSquareVelocityException(clientPresetPowerKeyForCircuits, value)) {
        return true;
      }
      const wt = collectorCharacteristicsSquareWallThickness(side);
      const v = calculateVelocitySquare(actualFlowRateForCircuits, side, wt);
      return v >= COLLECTOR_MIN_VELOCITY && v <= COLLECTOR_MAX_VELOCITY;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    actualFlowRateForCircuits,
    clientSizingLockedByPresetForCircuits,
    clientPresetPowerKeyForCircuits,
  ]);

  // ─── Оптимальные варианты ───────────────────────────────────────────────────
  const optimalDiameter = useMemo(() => {
    if (filteredDiameters.length === 0) return null;
    const targetV = COLLECTOR_BODY_TARGET_VELOCITY_MS;
    return filteredDiameters.reduce((best, d) => {
      const wt = collectorCharacteristicsRoundWallThickness(d.value);
      const v = calculateVelocityRound(actualFlowRate, d.value, wt);
      const bWt = collectorCharacteristicsRoundWallThickness(best.value);
      const bV = calculateVelocityRound(actualFlowRate, best.value, bWt);
      return Math.abs(v - targetV) < Math.abs(bV - targetV) ? d : best;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredDiameters, actualFlowRate]);

  const optimalProfile = useMemo(() => {
    if (filteredProfiles.length === 0) return null;
    const targetV = COLLECTOR_BODY_TARGET_VELOCITY_MS;
    return filteredProfiles.reduce((best, p) => {
      const wt = collectorCharacteristicsSquareWallThickness(p.side);
      const v = calculateVelocitySquare(actualFlowRate, p.side, wt);
      const bWt = collectorCharacteristicsSquareWallThickness(best.side);
      const bV = calculateVelocitySquare(actualFlowRate, best.side, bWt);
      return Math.abs(v - targetV) < Math.abs(bV - targetV) ? p : best;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredProfiles, actualFlowRate]);

  const optimalDiameterForCircuits = useMemo(() => {
    if (filteredDiametersForCircuits.length === 0) return null;
    const targetV = COLLECTOR_BODY_TARGET_VELOCITY_MS;
    return filteredDiametersForCircuits.reduce((best, d) => {
      const wt = collectorCharacteristicsRoundWallThickness(d.value);
      const v = calculateVelocityRound(actualFlowRateForCircuits, d.value, wt);
      const bWt = collectorCharacteristicsRoundWallThickness(best.value);
      const bV = calculateVelocityRound(actualFlowRateForCircuits, best.value, bWt);
      return Math.abs(v - targetV) < Math.abs(bV - targetV) ? d : best;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredDiametersForCircuits, actualFlowRateForCircuits]);

  const optimalProfileForCircuits = useMemo(() => {
    if (filteredProfilesForCircuits.length === 0) return null;
    const targetV = COLLECTOR_BODY_TARGET_VELOCITY_MS;
    return filteredProfilesForCircuits.reduce((best, p) => {
      const wt = collectorCharacteristicsSquareWallThickness(p.side);
      const v = calculateVelocitySquare(actualFlowRateForCircuits, p.side, wt);
      const bWt = collectorCharacteristicsSquareWallThickness(best.side);
      const bV = calculateVelocitySquare(actualFlowRateForCircuits, best.side, bWt);
      return Math.abs(v - targetV) < Math.abs(bV - targetV) ? p : best;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredProfilesForCircuits, actualFlowRateForCircuits]);

  // ─── Выбранные/итоговые значения ───────────────────────────────────────────
  const selectedDiameter = useMemo(() => {
    if (!isSquare) return characteristics.collectorDiameter ?? optimalDiameter?.value ?? null;
    return null;
  }, [isSquare, characteristics.collectorDiameter, optimalDiameter]);

  const selectedProfile = useMemo(() => {
    if (!isSquare) return null;
    if (compactPresetRow) {
      const m = COMPACT_DISTRIBUTION_SIZE_TO_PROFILES[compactPresetRow.compactSize];
      return m ? m.sheet : null;
    }
    return characteristics.collectorProfile ?? optimalProfile?.value ?? null;
  }, [isSquare, characteristics.collectorProfile, optimalProfile, compactPresetRow]);

  const selectedDiameterForCircuits = useMemo(() => {
    if (!isSquare) return characteristics.collectorDiameter ?? optimalDiameterForCircuits?.value ?? null;
    return null;
  }, [isSquare, characteristics.collectorDiameter, optimalDiameterForCircuits]);

  const selectedProfileForCircuits = useMemo(() => {
    if (!isSquare) return null;
    if (compactPresetRow) {
      const m = COMPACT_DISTRIBUTION_SIZE_TO_PROFILES[compactPresetRow.compactSize];
      return m ? m.sheet : null;
    }
    return characteristics.collectorProfile ?? optimalProfileForCircuits?.value ?? null;
  }, [isSquare, characteristics.collectorProfile, optimalProfileForCircuits, compactPresetRow]);

  const squareProfileForCircuitClamp = useMemo(() => {
    if (!isSquare) return null;
    if (compactPresetRow) return compactPresetRow.compactSize;
    return selectedProfileForCircuits;
  }, [isSquare, compactPresetRow, selectedProfileForCircuits]);

  /** Как в редакторе параметров при isCompact: проверка контуров по compactSize (60×60 / 80×80), а не по листу 60×30 / 80×40. */
  const squareProfileForCircuitFittingRules = useMemo(() => {
    if (!isSquare) return null;
    if (compactPresetRow) return compactPresetRow.compactSize;
    return selectedProfile;
  }, [isSquare, compactPresetRow, selectedProfile]);

  const clientCollectorSizeLabel = useMemo(() => {
    if (isSquare && characteristics.useCompactDistribution && compactPresetRow) {
      const m = COMPACT_DISTRIBUTION_SIZE_TO_PROFILES[compactPresetRow.compactSize];
      if (m) {
        return `Компакт ${compactPresetRow.compactSize.replace('x', '×')} (подача ${m.sheet.replace('x', '×')}, обратка ${m.profile.replace('x', '×')})`;
      }
    }
    if (!isSquare && selectedDiameter != null) {
      const wt = collectorCharacteristicsRoundWallThickness(selectedDiameter);
      const inner = selectedDiameter - 2 * wt;
      return `⌀${selectedDiameter} (внутр. ⌀${inner})`;
    }
    if (isSquare && selectedProfile) {
      const prof = COLLECTOR_CHARACTERISTICS_SQUARE_PROFILES.find((p) => p.value === selectedProfile);
      const side = prof?.side ?? parseInt(String(selectedProfile).split('x')[0], 10);
      if (!Number.isFinite(side)) return String(selectedProfile);
      const wt = collectorCharacteristicsSquareWallThickness(side);
      const inner = side - 2 * wt;
      return prof ? `${prof.label} (внутр. ${inner}×${inner})` : `${selectedProfile} (внутр. ${inner}×${inner})`;
    }
    return 'Определяется автоматически';
  }, [isSquare, selectedDiameter, selectedProfile, characteristics.useCompactDistribution, compactPresetRow]);

  const endConnectionCategoryForClamp =
    characteristics.endConnectionCategory ?? DEFAULT_FITTING_CATEGORY;

  // Подача и обратка: подбор совместимости по сечению из зафиксированных кВт/м³·ч (обновление при blur и смене режима формы)

  useEffect(() => {
    if (isSquare) {
      if (!squareProfileForCircuitClamp) return;
      const profileKey = squareProfileForCircuitClamp;
      const circuitCompat = (id) =>
        isDistributionCircuitFittingCompatibleWithSquareProfile(id, profileKey);
      setSupplyCircuits((prev) => clampCircuitsToCompatibility(prev, circuitCompat));
      setReturnCircuits((prev) => clampCircuitsToCompatibility(prev, circuitCompat));
      return;
    }
    if (selectedDiameterForCircuits == null) return;
    const circuitCompat = (id) => isCircuitFittingCompatible(id, selectedDiameterForCircuits);
    setSupplyCircuits((prev) => clampCircuitsToCompatibility(prev, circuitCompat));
    setReturnCircuits((prev) => clampCircuitsToCompatibility(prev, circuitCompat));
  }, [
    isSquare,
    selectedDiameterForCircuits,
    squareProfileForCircuitClamp,
    supplyCircuits.length,
    returnCircuits.length,
  ]);

  // Торцевые контуры гребёнки: актуальное сечение (торцевое подключение источника не привязываем к отложенному вводу)

  useEffect(() => {
    if (isSquare) {
      if (!squareProfileForCircuitFittingRules) return;
      const profileKey = squareProfileForCircuitFittingRules;
      const endCompat = (id) => isEndFittingCompatibleWithSquareProfile(id, profileKey);
      setLeftEndCircuit((prev) =>
        clampEndCircuitToCompatibility(prev, endCompat, endConnectionCategoryForClamp));
      setRightEndCircuit((prev) =>
        clampEndCircuitToCompatibility(prev, endCompat, endConnectionCategoryForClamp));
      return;
    }
    if (selectedDiameter == null) return;
    const endCompat = (id) => isEndFittingCompatibleWithMainPipe(id, selectedDiameter);
    setLeftEndCircuit((prev) =>
      clampEndCircuitToCompatibility(prev, endCompat, endConnectionCategoryForClamp));
    setRightEndCircuit((prev) =>
      clampEndCircuitToCompatibility(prev, endCompat, endConnectionCategoryForClamp));
  }, [isSquare, selectedDiameter, squareProfileForCircuitFittingRules, endConnectionCategoryForClamp]);

  const endConnectionType = categoryLabelToConnectionType(characteristics.endConnectionCategory);

  const filteredEndConnectionSizes = useMemo(() => {
    return filterCollectorEndConnectionSizes({
      actualFlowM3h: actualFlowRate,
      connectionType: endConnectionType,
      isSquare,
      manifoldRoundOd: !isSquare ? selectedDiameter : null,
      manifoldSquareProfile: isSquare ? selectedProfile : null,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    actualFlowRate,
    endConnectionType,
    isSquare,
    selectedDiameter,
    selectedProfile,
  ]);

  const optimalEndConnectionSizeKey = useMemo(() => {
    if (filteredEndConnectionSizes.length === 0) return null;
    return pickOptimalConnectionSizeKey(
      filteredEndConnectionSizes,
      endConnectionType,
      actualFlowRate,
      sourceConnectionTargetVelocityMs
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredEndConnectionSizes, endConnectionType, actualFlowRate, sourceConnectionTargetVelocityMs]);

  const currentVelocity = useMemo(() => {
    if (!isSquare && selectedDiameter) {
      const wt = collectorCharacteristicsRoundWallThickness(selectedDiameter);
      return calculateVelocityRound(actualFlowRate, selectedDiameter, wt);
    }
    if (isSquare && selectedProfile) {
      const prof = availableSquareProfiles.find(p => p.value === selectedProfile);
      if (prof) {
        const wt = collectorCharacteristicsSquareWallThickness(prof.side);
        return calculateVelocitySquare(actualFlowRate, prof.side, wt);
      }
      const outerMm = parseInt(String(selectedProfile).split('x')[0], 10);
      if (Number.isFinite(outerMm) && outerMm > 0) {
        const wt = collectorCharacteristicsSquareWallThickness(outerMm);
        return calculateVelocitySquare(actualFlowRate, outerMm, wt);
      }
    }
    return null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSquare, selectedDiameter, selectedProfile, actualFlowRate]);

  const hasValidDiameter = isSquare
    ? Boolean(compactPresetRow || selectedProfile)
    : Boolean(selectedDiameter);

  const needsEndConnection =
    characteristics.connectionSide !== 'none' &&
    !(isSquare && characteristics.useCompactDistribution);

  const effectiveEndConnectionSizeKey = useMemo(() => {
    if (!needsEndConnection) return null;
    if (characteristics.endConnectionSize != null) return characteristics.endConnectionSize;
    return optimalEndConnectionSizeKey;
  }, [
    needsEndConnection,
    characteristics.endConnectionSize,
    optimalEndConnectionSizeKey,
  ]);

  const hasValidConnectionSelection =
    !needsEndConnection ||
    (effectiveEndConnectionSizeKey != null && filteredEndConnectionSizes.length > 0);

  const connectionVelocityDisplay = useMemo(() => {
    if (!needsEndConnection || effectiveEndConnectionSizeKey == null) return null;
    return velocityForConnectionSize(
      endConnectionType,
      effectiveEndConnectionSizeKey,
      actualFlowRate
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsEndConnection, effectiveEndConnectionSizeKey, endConnectionType, actualFlowRate]);

  const computeMaxAvailablePowerKw = (c) => {
    const body = getMaxThermalPowerKwCollectorCharacteristics(c.shape, c.deltaTemp);
    const dt = Number(c.deltaTemp);
    if (!Number.isFinite(dt) || dt <= 0) return body;
    // Квадрат + торец: min(корпус, макс. расход по лучшему из допустимых типов подключений для 150×150) — без привязки к выбранной категории в форме.
    if (c.shape !== 'square' || c.connectionSide === 'none') return body;
    const qBody = ((body * 1000) / (WATER_DENSITY * WATER_SPECIFIC_HEAT * dt)) * 3600;
    const qEnd = getMaxEndConnectionFlowM3hDistributionCollectorSquareMaxProfile();
    if (!(qEnd > 0)) return body;
    const q = Math.min(qBody, qEnd);
    return Math.floor(((q / 3600) * WATER_DENSITY * WATER_SPECIFIC_HEAT * dt) / 1000);
  };

  const clampPowerAndFlowToMax = (next) => {
    if (next.shape === 'square' && next.useCompactDistribution) return;
    const dtMax = normalizeClientThermalDeltaTemp(next.deltaTemp);
    const maxP = computeMaxAvailablePowerKw({ ...next, deltaTemp: dtMax });
    if (next.inputMode === 'power' && typeof next.power === 'number' && !Number.isNaN(next.power) && next.power > maxP) {
      next.power = maxP;
    }
    if (next.inputMode === 'flowRate' && next.flowRate != null && dtMax) {
      const fp = calculatePowerFromFlowRate(next.flowRate, dtMax);
      if (fp > maxP) {
        next.flowRate = parseFloat(calculateFlowRateFromPower(maxP, dtMax).toFixed(2));
      }
    }
  };

  // Сброс вручную выбранного диаметра/профиля если вышел из диапазона (по зафиксированным Вт — не пока допечатывают число)
  useEffect(() => {
    if (!isSquare && characteristics.collectorDiameter) {
      const ok = filteredDiametersForCircuits.some(d => d.value === characteristics.collectorDiameter);
      const presetDiam =
        viewMode === 'clients' &&
        clientPresetPowerKeyForCircuits != null &&
        clientUsesPresetCollectorSize(charForCircuitSizing)
          ? DISTRIBUTION_COLLECTOR_CLIENT_PARAMS_TABLE[clientPresetPowerKeyForCircuits]?.diameter
          : null;
      if (!ok && characteristics.collectorDiameter !== presetDiam) {
        setCharacteristics(prev => ({ ...prev, collectorDiameter: null }));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filteredDiametersForCircuits,
    isSquare,
    viewMode,
    clientPresetPowerKeyForCircuits,
    characteristics.collectorDiameter,
  ]);

  useEffect(() => {
    if (isSquare && characteristics.useCompactDistribution) return;
    if (isSquare && characteristics.collectorProfile) {
      const ok = filteredProfilesForCircuits.some(p => p.value === characteristics.collectorProfile);
      const presetProf =
        viewMode === 'clients' &&
        clientPresetPowerKeyForCircuits != null &&
        clientUsesPresetCollectorSize(charForCircuitSizing)
          ? DISTRIBUTION_COLLECTOR_CLIENT_PARAMS_TABLE[clientPresetPowerKeyForCircuits]?.squareProfile
          : null;
      if (!ok && characteristics.collectorProfile !== presetProf) {
        setCharacteristics(prev => ({ ...prev, collectorProfile: null }));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filteredProfilesForCircuits,
    isSquare,
    viewMode,
    clientPresetPowerKeyForCircuits,
    characteristics.collectorProfile,
    characteristics.useCompactDistribution,
  ]);

  useEffect(() => {
    if (clientCatalogConnectionPresetOn) return;
    if (characteristics.endConnectionSize == null) return;
    const ok = filteredEndConnectionSizes.some((s) => s.diameter === characteristics.endConnectionSize);
    if (!ok) setCharacteristics(prev => ({ ...prev, endConnectionSize: null }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredEndConnectionSizes, clientCatalogConnectionPresetOn]);

  // Нет размеров для выбранной категории (напр. ниппель при большом расходе) — первая подходящая категория, как у комбинированного узла
  useEffect(() => {
    if (characteristics.connectionSide === 'none') return;
    const manifoldOk = isSquare ? Boolean(selectedProfile) : selectedDiameter != null;
    if (!manifoldOk) return;
    if (!(actualFlowRate > 0)) return;
    if (filteredEndConnectionSizes.length > 0) return;

    const currentType = categoryLabelToConnectionType(
      characteristics.endConnectionCategory ?? DEFAULT_FITTING_CATEGORY
    );

    const fallbackTypeOrder = clientFlangeOnlySourceConnection
      ? ['flange']
      : viewMode === 'clients'
        ? ['flange', 'nipple']
        : COLLECTOR_END_CONNECTION_TYPE_AUTO_FALLBACK_ORDER;

    for (const ct of fallbackTypeOrder) {
      if (ct === currentType) continue;
      const list = filterCollectorEndConnectionSizes({
        actualFlowM3h: actualFlowRate,
        connectionType: ct,
        isSquare,
        manifoldRoundOd: !isSquare ? selectedDiameter : null,
        manifoldSquareProfile: isSquare ? selectedProfile : null,
      });
      if (list.length > 0) {
        const row = COLLECTOR_END_CONNECTION_CATEGORIES.find((c) => c.connectionType === ct);
        const label = row?.id;
        if (!label) continue;
        setCharacteristics((prev) =>
          prev.endConnectionCategory === label
            ? prev
            : { ...prev, endConnectionCategory: label, endConnectionSize: null }
        );
        return;
      }
    }
  }, [
    characteristics.connectionSide,
    characteristics.endConnectionCategory,
    isSquare,
    selectedProfile,
    selectedDiameter,
    actualFlowRate,
    filteredEndConnectionSizes.length,
    viewMode,
    clientPresetPowerKey,
    clientFlangeOnlySourceConnection,
  ]);

  // Пресеты для клиентов: ⌀/профиль и подключение источника по шагам мощности (как у гидроразделителя).
  // Ключ пресета отстаёт до blur. Свыше каталога живой расход уже требует фланец, а ключ ещё старый:
  // смена категории заново запускала этот эффект и возвращала ниппель из таблицы.
  useEffect(() => {
    if (viewMode !== 'clients') {
      prevClientPresetPowerKeyRef.current = null;
      prevClientPresetShapeRef.current = null;
      return;
    }
    if (characteristics.useCompactDistribution) {
      return;
    }
    if (clientFlangeOnlySourceConnection) {
      prevClientPresetPowerKeyRef.current = null;
      return;
    }
    if (clientPresetPowerKeyForCircuits == null) {
      prevClientPresetPowerKeyRef.current = null;
      return;
    }

    const raw =
      DISTRIBUTION_COLLECTOR_CLIENT_PARAMS_TABLE[clientPresetPowerKeyForCircuits];
    if (!raw) return;

    const keyChanged =
      prevClientPresetPowerKeyRef.current !== clientPresetPowerKeyForCircuits;
    const shapeChanged =
      prevClientPresetShapeRef.current != null &&
      prevClientPresetShapeRef.current !== characteristics.shape;

    const shouldFullApply = keyChanged || shapeChanged;

    const isSq = characteristics.shape === 'square';

    setCharacteristics((prev) => {
      const next = { ...prev };
      let changed = false;

      if (shouldFullApply && clientUsesPresetCollectorSize(prev)) {
        if (!isSq && raw.diameter != null && prev.collectorDiameter !== raw.diameter) {
          next.collectorDiameter = raw.diameter;
          changed = true;
        }
        if (isSq && raw.squareProfile != null && prev.collectorProfile !== raw.squareProfile) {
          next.collectorProfile = raw.squareProfile;
          changed = true;
        }
        const presetP = resolvePresetPressure(raw, isSq);
        if (Number(prev.pressure) !== Number(presetP)) {
          next.pressure = presetP;
          changed = true;
        }
      }

      if (
        clientUsesCatalogPresetConnection(prev, {
          overTable: false,
          constructionKeyKw: clientPresetPowerKeyForCircuits,
        })
      ) {
        const categoryLabel = getDistributionCollectorPresetEndConnectionCategory(
          clientPresetPowerKeyForCircuits
        );
        const connectionSize = getDistributionCollectorPresetEndConnectionSize(
          clientPresetPowerKeyForCircuits,
          categoryLabel
        );
        if (prev.endConnectionCategory !== categoryLabel) {
          next.endConnectionCategory = categoryLabel;
          changed = true;
        }
        if (connectionSize != null && prev.endConnectionSize !== connectionSize) {
          next.endConnectionSize = connectionSize;
          changed = true;
        }
      }

      return changed ? next : prev;
    });

    prevClientPresetPowerKeyRef.current = clientPresetPowerKeyForCircuits;
    prevClientPresetShapeRef.current = characteristics.shape;
  }, [
    clientPresetPowerKeyForCircuits,
    clientFlangeOnlySourceConnection,
    viewMode,
    characteristics.shape,
    characteristics.endConnectionCategory,
    characteristics.useCompactDistribution,
    characteristics.clientPresetConnectionType,
    characteristics.clientPresetCollectorSize,
  ]);

  // Свыше каталога и галочка стандартных изделий: фиксируем ⌀/профиль автоподбором (~0,6 м/с).
  useEffect(() => {
    if (viewMode !== 'clients' || characteristics.useCompactDistribution) return;
    if (!clientPresetCollectorSizeOn || !clientFlangeOnlySourceConnection) return;
    if (clientPresetPowerKey != null) return;
    if (isSquare) {
      const prof = optimalProfile?.value;
      if (!prof) return;
      setCharacteristics((prev) => {
        if (!clientUsesPresetCollectorSize(prev) || prev.collectorProfile === prof) return prev;
        return { ...prev, collectorProfile: prof };
      });
      return;
    }
    const d = optimalDiameter?.value;
    if (d == null) return;
    setCharacteristics((prev) => {
      if (!clientUsesPresetCollectorSize(prev) || prev.collectorDiameter === d) return prev;
      return { ...prev, collectorDiameter: d };
    });
  }, [
    viewMode,
    clientPresetCollectorSizeOn,
    clientFlangeOnlySourceConnection,
    clientPresetPowerKey,
    isSquare,
    optimalDiameter,
    optimalProfile,
    characteristics.useCompactDistribution,
  ]);

  // Круг: прямая/6 бар только при ⌀ ≤ 60.3 и наличии прямой в каталоге; иначе — 8/10
  useEffect(() => {
    if (isSquare || selectedDiameter == null) return;
    if (!findStraightCap(selectedDiameter) || selectedDiameter > 60.3) {
      setCharacteristics(prev => {
        if (Number(prev.pressure) === 8) return prev;
        return { ...prev, pressure: 8 };
      });
    }
  }, [isSquare, selectedDiameter]);

  const charShape = characteristics.shape;
  const charConnectionSide = characteristics.connectionSide;
  const maxAvailablePowerKw = useMemo(
    () =>
      computeMaxAvailablePowerKw({
        shape: charShape,
        deltaTemp: effectiveThermalDeltaTemp,
        connectionSide: charConnectionSide,
      }),
    [charShape, effectiveThermalDeltaTemp, charConnectionSide]
  );

  const maxFlowRateForPowerLimit = useMemo(() => {
    if (!effectiveThermalDeltaTemp) return Number.POSITIVE_INFINITY;
    return calculateFlowRateFromPower(maxAvailablePowerKw, effectiveThermalDeltaTemp);
  }, [maxAvailablePowerKw, effectiveThermalDeltaTemp]);

  // ─── Обновление характеристик ───────────────────────────────────────────────
  const updateCharacteristic = (key, value, options) => {
    setCharacteristics(prev => {
      const thermalDtForPrev = () => normalizeClientThermalDeltaTemp(prev.deltaTemp);
      const next = { ...prev, [key]: value };

      if (viewMode === 'clients' && key === 'power' && options?.finalizeClientPreset) {
        const p = typeof next.power === 'number' ? next.power : parseFloat(next.power);
        if (Number.isFinite(p)) {
          applyClientDistributionPowerPresetSnap(next, p);
        }
      }

      if (key === 'deltaTemp') {
        const dt = Number(value);
        if (COLLECTOR_ENGINEERS_DELTA_TEMP_CHOICES.includes(dt)) {
          if (viewMode === 'engineers') {
            preservedEngineersDeltaTempRef.current = dt;
          }
        }
      }

      if (viewMode === 'clients' && key === 'clientPresetDeltaTemp') {
        next.clientPresetDeltaTemp = Boolean(value);
        if (next.inputMode === 'power') {
          syncClientDistributionThermalFlowFromPower(next);
        }
        applyClientDistributionDeltaTempFromConstructionPreset(next);
      }

      if (viewMode === 'clients' && key === 'clientPresetConnectionType') {
        next.clientPresetConnectionType = Boolean(value);
        if (next.clientPresetConnectionType) {
          applyClientDistributionConnectionFromConstructionPreset(next);
        } else {
          seedClientDistributionConnectionFromCatalog(next);
        }
      }

      if (viewMode === 'clients' && key === 'clientPresetCollectorSize') {
        next.clientPresetCollectorSize = Boolean(value);
        if (next.clientPresetCollectorSize) {
          applyClientDistributionCollectorFromConstructionPreset(next);
        } else {
          seedClientDistributionCollectorFromCatalog(next);
        }
      }

      if (viewMode === 'clients' && key === 'deltaTemp') {
        if (clientUsesPresetDeltaTemp(next)) {
          return prev;
        }
        next.deltaTemp = normalizeClientThermalDeltaTemp(Number(value));
        if (next.inputMode === 'power') {
          syncClientDistributionThermalFlowFromPower(next);
        }
      }

      if (viewMode === 'clients' && key === 'flowRate') {
        const qn =
          typeof next.flowRate === 'number' ? next.flowRate : parseFloat(next.flowRate);
        if (Number.isFinite(qn) && qn > 0) {
          const presetKey = distributionCollectorClientPresetKeyFromFlow(
            qn,
            normalizeClientThermalDeltaTemp(next.deltaTemp),
            next.shape
          );
          if (presetKey != null && clientUsesPresetDeltaTemp(next)) {
            next.deltaTemp = getDistributionCollectorPresetReferenceDeltaTemp(presetKey);
          }
        }
      }
      if (key === 'inputMode' && value === 'flowRate') {
        const dt = thermalDtForPrev();
        if (viewMode === 'clients') {
          const pRaw = prev.power;
          const pn =
            typeof pRaw === 'number' && !Number.isNaN(pRaw) && pRaw > 0 ? pRaw : Number(pRaw);
          next.flowRate = parseFloat(
            calculateFlowRateFromPower(Number.isFinite(pn) && pn > 0 ? pn : 0, dt).toFixed(2)
          );
        } else {
          const pForFlow = prev.power;
          next.flowRate = parseFloat(calculateFlowRateFromPower(pForFlow, dt).toFixed(2));
        }
      }
      if (key === 'inputMode' && value === 'power') {
        const dt = thermalDtForPrev();
        if (viewMode === 'clients') {
          const q = prev.flowRate;
          const qn = typeof q === 'number' && !Number.isNaN(q) ? q : Number(q);
          const presetKey =
            Number.isFinite(qn) && qn > 0
              ? distributionCollectorClientPresetKeyFromFlow(
                  qn,
                  normalizeClientThermalDeltaTemp(prev.deltaTemp),
                  prev.shape
                )
              : null;
          if (presetKey != null) {
            next.power = clampCollectorClientPowerKw(
              parseFloat(calculatePowerFromFlowRate(prev.flowRate, dt).toFixed(2)),
              COLLECTOR_CLIENT_POWER_STEPS_MAX
            );
            syncClientDistributionThermalFlowFromPower(next);
            applyClientDistributionDeltaTempFromConstructionPreset(next);
            applyClientDistributionConnectionFromConstructionPreset(next);
          } else {
            next.power = parseFloat(calculatePowerFromFlowRate(prev.flowRate, dt).toFixed(2));
          }
        } else {
          next.power = parseFloat(calculatePowerFromFlowRate(prev.flowRate, dt).toFixed(2));
        }
      }
      // При смене сечения — сбрасываем выбранный диаметр/профиль и давление по умолчанию
      if (key === 'shape') {
        next.collectorDiameter = null;
        next.collectorProfile = null;
        next.endConnectionSize = null;
        next.pressure = value === 'square' ? 4 : 6;
        next.useCompactDistribution = false;
        next.compactDistributionPresetKw = 60;
        clampPowerAndFlowToMax(next);
      }
      if (key === 'deltaTemp') {
        clampPowerAndFlowToMax(next);
      }
      if (key === 'connectionSide') {
        clampPowerAndFlowToMax(next);
      }
      if (key === 'endConnectionCategory') {
        next.endConnectionSize = null;
      }
      if (key === 'pressure' && next.shape === 'round' && next.collectorDiameter != null) {
        const od = Number(next.collectorDiameter);
        if (!Number.isNaN(od) && (od > 60.3 || !findStraightCap(od)) && Number(value) === 6) {
          next.pressure = 8;
        }
      }
      if (key === 'collectorDiameter' && next.shape === 'round' && value != null) {
        const od = Number(value);
        if (!Number.isNaN(od) && (od > 60.3 || !findStraightCap(od)) && Number(next.pressure) === 6) {
          next.pressure = 8;
        }
      }
      return next;
    });
  };

  // ─── Вычисляем отступы (первый/последний контур гребёнки; торцы не учитываются) ─
  const computeOffsets = () => {
    const profileSideMm =
      isSquare && selectedProfile
        ? parseInt(String(selectedProfile).split('x')[0], 10)
        : null;
    const { firstOffset, lastOffset } = getDistributionCollectorFirstLastOffsetsMm({
      supplyCircuits,
      returnCircuits,
      mainPipeOuterMm: isSquare ? null : selectedDiameter,
      isSquare,
      profileSideMm: Number.isFinite(profileSideMm) ? profileSideMm : null,
    });
    return { firstOffset, lastOffset };
  };

  /** Центральный фитинг по умолчанию: «Между патрубками», индекс — середина списка (как в CenterFittingsUI). */
  const buildDefaultCenterFitting = (circuitCount, referenceRow) => {
    const base = {
      id: 1,
      fittingCategory: 'Муфты',
      fittingId: 'Mufta_1.2',
      side: 'both',
      distanceFromStart: 50,
      distanceDirection: 'left',
      betweenCircuits: '1-2',
      referenceRow,
    };
    const optionsBetweenFittings = Math.max(0, circuitCount * 2 - 1);
    if (optionsBetweenFittings >= 1) {
      return {
        ...base,
        positionType: 'betweenFittings',
        betweenIndex: Math.floor((optionsBetweenFittings - 1) / 2),
      };
    }
    return { ...base, positionType: 'center', betweenIndex: 0 };
  };

  // ─── Расчёт параметров ──────────────────────────────────────────────────────
  const calculateParameters = () => {
    if (!hasValidDiameter) return;
    if (needsEndConnection && effectiveEndConnectionSizeKey == null) return;

    const compactCalcRow =
      isSquare && characteristics.useCompactDistribution
        ? COMPACT_DISTRIBUTION_COLLECTOR_PRESETS.find(
            (p) => p.powerKw === characteristics.compactDistributionPresetKw
          ) ?? COMPACT_DISTRIBUTION_COLLECTOR_PRESETS[0]
        : null;

    /** Фиксированный вылет 100 мм только у фланцев; для накидных гаек/шайб и пр. — авто (null). */
    const flangeStubPipeLengthMm = (fittingId) =>
      fittingId && String(fittingId).startsWith('Flanec_') ? 100 : null;

    const { connectionSide } = characteristics;
    const hasLeft =
      compactCalcRow != null
        ? false
        : connectionSide === 'left' || connectionSide === 'both';
    const hasRight =
      compactCalcRow != null
        ? false
        : connectionSide === 'right' || connectionSide === 'both';
    const supplyN = supplyCircuits.length;
    const returnN = returnCircuits.length;
    const circuitCount = Math.max(supplyN, returnN);
    // Центральные фитинги: привязка к линии с большим числом контуров (и середина списка по ней)
    const centerRefRow = supplyN >= returnN ? 'supply' : 'return';
    const centerRefCount = centerRefRow === 'supply' ? supplyN : returnN;
    const defaultCenterFitting = buildDefaultCenterFitting(centerRefCount, centerRefRow);

    // Доп. выходы (муфта ½", вылет авто): круг — 25 мм при ⌀ ≤ 60.3, 35 мм при ⌀ > 60.3;
    // квадрат (не компакт) — то же правило по наружной стороне профиля: ≤ 60×60 → 25 мм, > 60×60 → 35 мм (60×60 ≈ ⌀ 60.3).
    // Компактный квадрат — без автодобавления (как раньше только круг имел выходы из калькулятора).
    const squareProfileSideMmForOutletRule =
      isSquare && selectedProfile && compactCalcRow == null
        ? parseInt(String(selectedProfile).split('x')[0], 10)
        : NaN;
    const additionalOutletDistanceFromEdge = (() => {
      if (!isSquare && selectedDiameter != null) {
        return selectedDiameter > 60.3 ? 35 : 25;
      }
      if (isSquare && compactCalcRow == null && Number.isFinite(squareProfileSideMmForOutletRule)) {
        return squareProfileSideMmForOutletRule > 60 ? 35 : 25;
      }
      return 25;
    })();
    const addAutoAdditionalOutlets =
      (!isSquare && selectedDiameter != null) ||
      (isSquare && compactCalcRow == null && Number.isFinite(squareProfileSideMmForOutletRule));
    const defaultSupplyAdditionalOutlet = {
      id: 1,
      type: 'fitting',
      diameter: 26.9,
      length: 80,
      fittingCategory: 'Муфты',
      fittingId: 'Mufta_1.2',
      fittingLength: null,
      flangePipeLength: null,
      direction: 'up',
      positionType: 'distance',
      distanceFromEdge: additionalOutletDistanceFromEdge,
      distanceDirection: 'right',
      betweenIndex: 0,
      referenceRow: 'supply',
      outletType: 'short',
    };
    const defaultReturnAdditionalOutlet = {
      ...defaultSupplyAdditionalOutlet,
      id: 1,
      direction: 'down',
      referenceRow: 'return',
      distanceDirection: 'left',
    };

    let capType, shapeFields;

    // Прямая заглушка и режим 6/8 — только круг ⌀ ≤ 60.3 при наличии прямой в каталоге
    const roundStraightCapAllowed =
      !isSquare &&
      selectedDiameter != null &&
      selectedDiameter <= 60.3 &&
      Boolean(findStraightCap(selectedDiameter));

    if (!isSquare) {
      capType =
        roundStraightCapAllowed && Number(characteristics.pressure) < 8
          ? 'straight'
          : 'elliptical';
      shapeFields = {
        shape: 'round',
        supplyPipeDiameter: selectedDiameter,
        returnPipeDiameter: selectedDiameter,
        supplyPipeProfile: '80x80',
        returnPipeProfile: '80x80',
      };
    } else {
      capType = 'straight';
      if (compactCalcRow) {
        const mapping = COMPACT_DISTRIBUTION_SIZE_TO_PROFILES[compactCalcRow.compactSize];
        shapeFields = {
          shape: 'square',
          supplyPipeDiameter: 76.1,
          returnPipeDiameter: 76.1,
          supplyPipeProfile: mapping.sheet,
          returnPipeProfile: mapping.profile,
        };
      } else {
        shapeFields = {
          shape: 'square',
          supplyPipeDiameter: 76.1,      // дефолт для round-полей (не используется)
          returnPipeDiameter: 76.1,
          supplyPipeProfile: selectedProfile,
          returnPipeProfile: selectedProfile,
        };
      }
    }

    let firstOffset;
    let lastOffset;
    if (compactCalcRow) {
      const edge = COMPACT_DISTRIBUTION_FIRST_LAST_OFFSET_MM[compactCalcRow.compactSize];
      firstOffset = edge;
      lastOffset = edge;
    } else {
      const o = computeOffsets();
      firstOffset = o.firstOffset;
      lastOffset = o.lastOffset;
    }
    const supplySpacingLayout = computeDistributionCircuitSpacingLayout(supplyCircuits);
    const returnSpacingLayout = computeDistributionCircuitSpacingLayout(returnCircuits);
    const supplyDefaultBetween =
      supplySpacingLayout.spacingToNext.find((x) => x != null) ??
      supplySpacingLayout.bases[0] ??
      125;
    const returnDefaultBetween =
      returnSpacingLayout.spacingToNext.find((x) => x != null) ??
      returnSpacingLayout.bases[0] ??
      125;

    const categoryLabel = characteristics.endConnectionCategory;
    const connType = endConnectionType;
    const sizeKey = effectiveEndConnectionSizeKey;
    const thermalPowerKw =
      compactCalcRow != null
        ? compactCalcRow.powerKw
        : characteristics.inputMode === 'power'
          ? (Number(characteristics.power) || 0)
          : (viewMode === 'clients' && clientPresetPowerKey != null
              ? clientPresetPowerKey
              : calculatePowerFromFlowRate(characteristics.flowRate, effectiveThermalDeltaTemp));
    const pipeSpacingFromSeparatorGeometry =
      viewMode === 'clients' &&
      clientFlangeOnlySourceConnection &&
      needsEndConnection &&
      sizeKey != null &&
      actualFlowRate > 0
        ? computeDistributionClientOverTablePipeSpacingFromSeparatorGeometryMm({
            actualFlowRateM3h: actualFlowRate,
            isSquare,
            endConnectionType: connType,
            endConnectionSizeKey: sizeKey,
          })
        : null;
    const pipeSpacingBetweenCollectors =
      pipeSpacingFromSeparatorGeometry != null
        ? pipeSpacingFromSeparatorGeometry
        : needsEndConnection && sizeKey != null
          ? getPipeSpacingBetweenSupplyReturnMm(connType, sizeKey, { thermalPowerKw })
          : 200;
    const fittingId = needsEndConnection && sizeKey != null
      ? getFittingIdFromConnection(connType, sizeKey)
      : '';
    const transitionOd = needsEndConnection && sizeKey != null
      ? getTransitionPipeDiameter(connType, sizeKey)
      : (isSquare ? 76.1 : selectedDiameter);

    const endCircuitForSide = (prefix) => {
      const isLeft = prefix.endsWith('Left');
      return isLeft ? leftEndCircuit : rightEndCircuit;
    };

    const makePipeConn = (prefix, enabled) => {
      const p = prefix.charAt(0).toLowerCase() + prefix.slice(1);
      const endCircuit = endCircuitForSide(prefix);

      // Торцевой контур на этой стороне всегда активирует подключение
      const hasEndCircuitHere = Boolean(endCircuit);
      const isActive = enabled || hasEndCircuitHere;

      if (!isActive) {
        return {
          [`has${prefix}Connection`]: false,
          [`${p}ConnectionType`]: 'fitting',
          [`${p}ConnectionPipeDiameter`]: isSquare ? 76.1 : selectedDiameter,
          [`${p}ConnectionPipeLength`]: flangeStubPipeLengthMm(''),
          [`${p}ConnectionFittingCategory`]: '',
          [`${p}ConnectionFitting`]: '',
          [`${p}ConnectionFittingLength`]: null,
          [`${p}ConnectionLength`]: null,
        };
      }

      // Если задан торцевой контур — его фитинг; иначе авто-подбор по скорости
      const connFittingId       = endCircuit?.fitting       ?? fittingId;
      const connFittingCategory = endCircuit?.fittingCategory ?? categoryLabel;
      const connPipeDiameter    = endCircuit
        ? (() => {
            const ef = endCircuit.fitting;
            if (
              ef?.startsWith('Flanec_') ||
              ef?.startsWith('Nak_Gaika_') ||
              ef?.startsWith('Shaib_Nar_Rezba_')
            ) {
              return getPipeDiameterForFitting(ef);
            }
            return fittingsData[ef]?.diameter ?? (isSquare ? 76.1 : selectedDiameter);
          })()
        : transitionOd;
      return {
        [`has${prefix}Connection`]: true,
        [`${p}ConnectionType`]: 'fitting',
        [`${p}ConnectionPipeDiameter`]: connPipeDiameter,
        [`${p}ConnectionPipeLength`]: flangeStubPipeLengthMm(connFittingId),
        [`${p}ConnectionFittingCategory`]: connFittingCategory,
        [`${p}ConnectionFitting`]: connFittingId,
        [`${p}ConnectionFittingLength`]: null,
        [`${p}ConnectionLength`]: null,
      };
    };

    // Для торцевых подключений определяем нужен ли переход или заглушка
    const shape = isSquare ? 'square' : 'round';
    const pipeDiameter = isSquare ? 76.1 : selectedDiameter;

    const makeCap = (prefix, active) => {
      const p = prefix.charAt(0).toLowerCase() + prefix.slice(1);
      const endCircuit = endCircuitForSide(prefix);
      const hasEndCircuitHere = Boolean(endCircuit);
      const isActive = active || hasEndCircuitHere;
      const connFitId = endCircuit?.fitting ?? fittingId;
      if (!isActive || !connFitId) {
        return {
          [`has${prefix}Cap`]: !isActive,
          [`${p}CapElementType`]: 'cap',
          [`${p}CapType`]: capType,
          [`${p}Reducer`]: null,
        };
      }
      const side = prefix.endsWith('Left') ? 'left' : 'right';
      const collector = prefix.startsWith('Supply') ? 'supply' : 'return';
      const capUpdates = getCapUpdatesForConnectionFitting(connFitId, pipeDiameter, side, collector, shape);
      if (Object.keys(capUpdates).length === 0) {
        return {
          [`has${prefix}Cap`]: false,
          [`${p}CapElementType`]: 'cap',
          [`${p}CapType`]: capType,
          [`${p}Reducer`]: null,
        };
      }
      const merged = {
        [`${p}CapType`]: capType,
        [`${p}Reducer`]: null,
        ...capUpdates,
      };
      const capKey = `${p}CapType`;
      const elemKey = `${p}CapElementType`;
      const hasKey = `has${prefix}Cap`;
      const userStraight =
        shape === 'round' &&
        pipeDiameter <= 60.3 &&
        Boolean(findStraightCap(pipeDiameter)) &&
        Number(characteristics.pressure) < 8;
      if (
        merged[elemKey] !== 'reducer' &&
        userStraight &&
        merged[hasKey] === true &&
        merged[capKey] === 'elliptical'
      ) {
        merged[capKey] = 'straight';
      }
      return merged;
    };

    // Переход на торце подключения не отменяет режим 6/8 бар: свободный торец может быть с прямой заглушкой
    // (capType от давления; makeCap опускает elliptical→straight для сторон без reducer).

    const pipeSpacingCompactMm =
      compactCalcRow != null
        ? parseInt(
            COMPACT_DISTRIBUTION_SIZE_TO_PROFILES[compactCalcRow.compactSize].sheet.split('x')[1],
            10
          )
        : null;

    const params = {
      ...shapeFields,
      isCompact: Boolean(compactCalcRow),
      compactSize: compactCalcRow ? compactCalcRow.compactSize : '80x80',

      pipeSpacing: pipeSpacingCompactMm != null ? pipeSpacingCompactMm : pipeSpacingBetweenCollectors,
      ...(compactCalcRow != null ? { previousPipeSpacing: pipeSpacingBetweenCollectors } : {}),

      circuitCount,
      supplyCircuits: supplyCircuits.map((c, i) => ({
        id: `supply-${i}`,
        fitting: c.fitting,
        fittingCategory: c.fittingCategory,
        fittingLength: null,
        flangePipeLength: flangeStubPipeLengthMm(c.fitting),
        spacingToNext: supplySpacingLayout.spacingToNext[i],
        circuitSpacing: supplySpacingLayout.bases[i] ?? 125,
        swapOrder: false,
      })),
      returnCircuits: returnCircuits.map((c, i) => ({
        id: `return-${i}`,
        fitting: c.fitting,
        fittingCategory: c.fittingCategory,
        fittingLength: null,
        flangePipeLength: flangeStubPipeLengthMm(c.fitting),
        spacingToNext: returnSpacingLayout.spacingToNext[i],
        circuitSpacing: returnSpacingLayout.bases[i] ?? 125,
        swapOrder: false,
      })),
      supplyFirstCircuitOffset: firstOffset,
      supplyFirstCircuitOffsetMode: 'fromEdge',
      supplyFirstCircuitOffsetReferenceIndex: 0,
      supplyFirstCircuitOffsetSaved: null,
      supplyFirstCircuitOffsetLastUsed: 0,
      supplyLastCircuitOffset: lastOffset,
      returnFirstCircuitOffset: firstOffset,
      returnFirstCircuitOffsetMode: 'fromEdge',
      returnFirstCircuitOffsetReferenceIndex: 0,
      returnFirstCircuitOffsetSaved: null,
      returnFirstCircuitOffsetLastUsed: 0,
      returnLastCircuitOffset: lastOffset,
      supplyCircuitSpacing: supplySpacingLayout.bases[0] ?? 125,
      returnCircuitSpacing: returnSpacingLayout.bases[0] ?? 125,
      supplySpacingBetweenCircuits: supplyDefaultBetween,
      returnSpacingBetweenCircuits: returnDefaultBetween,

      ...makePipeConn('SupplyLeft',  hasLeft),
      ...makePipeConn('SupplyRight', hasRight),
      ...makePipeConn('ReturnLeft',  hasLeft),
      ...makePipeConn('ReturnRight', hasRight),

      ...makeCap('SupplyLeft',  hasLeft),
      ...makeCap('SupplyRight', hasRight),
      ...makeCap('ReturnLeft',  hasLeft),
      ...makeCap('ReturnRight', hasRight),

      supplyCenterFittingCount: compactCalcRow != null ? 0 : 1,
      returnCenterFittingCount: compactCalcRow != null ? 0 : 1,
      supplyCenterFittings:
        compactCalcRow != null ? [] : [{ ...defaultCenterFitting, id: 1 }],
      returnCenterFittings:
        compactCalcRow != null ? [] : [{ ...defaultCenterFitting, id: 1 }],
      supplyAdditionalOutletCount: addAutoAdditionalOutlets ? 1 : 0,
      returnAdditionalOutletCount: addAutoAdditionalOutlets ? 1 : 0,
      supplyAdditionalOutlets: addAutoAdditionalOutlets ? [defaultSupplyAdditionalOutlet] : [],
      returnAdditionalOutlets: addAutoAdditionalOutlets ? [defaultReturnAdditionalOutlet] : [],
      supplyAdditionalFirstOutletOffset: 60,
      returnAdditionalFirstOutletOffset: 60,
      supplyAdditionalLastOutletOffset: 60,
      returnAdditionalLastOutletOffset: 60,
      supplyAdditionalOutletSpacing: 100,
      returnAdditionalOutletSpacing: 100,

      useManualLength: false,
      manualSupplyLength: null,
      manualReturnLength: null,

      _calculatedFrom: 'characteristics',
      _calculatorViewMode: viewMode,
      _characteristics: {
        ...characteristics,
        useCompactDistribution: characteristics.useCompactDistribution,
        compactDistributionPresetKw: characteristics.compactDistributionPresetKw,
        supplyCircuits: supplyCircuits.map(({ fittingCategory, fitting }) => ({ fittingCategory, fitting })),
        returnCircuits: returnCircuits.map(({ fittingCategory, fitting }) => ({ fittingCategory, fitting })),
        leftEndCircuit:  leftEndCircuit  ? { fittingCategory: leftEndCircuit.fittingCategory,  fitting: leftEndCircuit.fitting  } : null,
        rightEndCircuit: rightEndCircuit ? { fittingCategory: rightEndCircuit.fittingCategory, fitting: rightEndCircuit.fitting } : null,
      },
    };

    onCalculate(params);
  };

  // ─── Вспомогательные для рендера ───────────────────────────────────────────
  const displayFlowRate =
    viewMode === 'clients' && clientThermalFlowM3h != null
      ? clientThermalFlowM3h.toFixed(2)
      : actualFlowRate.toFixed(2);
  const displayPower =
    characteristics.inputMode === 'flowRate'
      ? calculatePowerFromFlowRate(characteristics.flowRate, effectiveThermalDeltaTemp).toFixed(2)
      : null;

  const roundPipeNoSixEightPressure =
    !isSquare &&
    selectedDiameter != null &&
    (selectedDiameter > 60.3 || !findStraightCap(selectedDiameter));

  /** Оба активных торца с концентрическим переходом — только 8/10 бар (как при двух переходах подряд). */
  const bothSidesNeedConcentricReducer = useMemo(() => {
    if (isSquare || !selectedDiameter || !needsEndConnection) return false;
    const shape = 'round';
    const pipeDiameter = selectedDiameter;
    const cs = characteristics.connectionSide;
    const hasLeft = cs === 'left' || cs === 'both';
    const hasRight = cs === 'right' || cs === 'both';
    const sizeKey = effectiveEndConnectionSizeKey;
    const fittingId =
      needsEndConnection && sizeKey != null
        ? getFittingIdFromConnection(endConnectionType, sizeKey)
        : '';

    const sideNeedsReducer = (prefix) => {
      const isLeft = prefix.endsWith('Left');
      const enabled = isLeft ? hasLeft : hasRight;
      const endCircuit = isLeft ? leftEndCircuit : rightEndCircuit;
      const hasEndCircuitHere = Boolean(endCircuit);
      const isActive = enabled || hasEndCircuitHere;
      const connFitId = endCircuit?.fitting ?? fittingId;
      if (!isActive || !connFitId) return false;
      const side = isLeft ? 'left' : 'right';
      const capUpdates = getCapUpdatesForConnectionFitting(connFitId, pipeDiameter, side, 'supply', shape);
      const p = prefix.charAt(0).toLowerCase() + prefix.slice(1);
      return capUpdates[`${p}CapElementType`] === 'reducer';
    };

    return sideNeedsReducer('SupplyLeft') && sideNeedsReducer('SupplyRight');
  }, [
    isSquare,
    selectedDiameter,
    needsEndConnection,
    characteristics.connectionSide,
    effectiveEndConnectionSizeKey,
    endConnectionType,
    leftEndCircuit,
    rightEndCircuit,
  ]);

  useEffect(() => {
    if (!bothSidesNeedConcentricReducer) return;
    setCharacteristics((prev) => (Number(prev.pressure) === 8 ? prev : { ...prev, pressure: 8 }));
  }, [bothSidesNeedConcentricReducer]);

  const collectorDiameterSelectValue =
    characteristics.collectorDiameter ?? optimalDiameter?.value ?? filteredDiameters[0]?.value ?? '';
  const collectorProfileSelectValue = useMemo(() => {
    if (isSquare && characteristics.useCompactDistribution && compactPresetRow) {
      const m = COMPACT_DISTRIBUTION_SIZE_TO_PROFILES[compactPresetRow.compactSize];
      if (m) return m.sheet;
    }
    return characteristics.collectorProfile ?? optimalProfile?.value ?? filteredProfiles[0]?.value ?? '';
  }, [
    isSquare,
    characteristics.useCompactDistribution,
    characteristics.collectorProfile,
    compactPresetRow,
    optimalProfile,
    filteredProfiles,
  ]);
  const endConnectionSizeSelectValue =
    characteristics.endConnectionSize ?? optimalEndConnectionSizeKey ?? filteredEndConnectionSizes[0]?.diameter ?? '';

  const clientLockedEndConnectionSizeLabel = useMemo(() => {
    if (!needsEndConnection) return '—';
    const key =
      characteristics.endConnectionSize != null
        ? characteristics.endConnectionSize
        : effectiveEndConnectionSizeKey;
    if (key == null) return '—';
    const hit = filteredEndConnectionSizes.find((s) => s.diameter === key);
    if (hit?.displayName) return hit.displayName;
    const catalog = CONNECTION_SIZE_CATALOG.find((s) => s.diameter === key);
    return catalog?.displayName ?? String(key);
  }, [
    needsEndConnection,
    characteristics.endConnectionSize,
    effectiveEndConnectionSizeKey,
    filteredEndConnectionSizes,
  ]);

  const isCompactSquare = isSquare && characteristics.useCompactDistribution;

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)', py: 3 }}>
      <Container maxWidth="md">
        {/* Шапка */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton onClick={onBack} sx={{ bgcolor: 'white', boxShadow: 1, '&:hover': { bgcolor: 'grey.100' } }}>
                <HomeIcon />
              </IconButton>
              {onBackToDrawing && (
                <IconButton onClick={onBackToDrawing} sx={{ bgcolor: 'white', boxShadow: 1, '&:hover': { bgcolor: 'grey.100' } }}>
                  <ArrowForwardIcon />
                </IconButton>
              )}
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>Расчёт по характеристикам</Typography>
              <Typography variant="body2" color="text.secondary">Распределительный коллектор</Typography>
            </Box>
          </Box>
          {isAdmin && (
            <Tooltip title="Переключение между режимами для клиентов и инженеров">
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, val) => { if (val) setViewMode(val); }}
                size="small"
                sx={{ bgcolor: 'white', boxShadow: 1 }}
              >
                <ToggleButton value="clients" sx={{ px: 2, textTransform: 'none', fontSize: '0.8rem' }}>
                  Для клиентов
                </ToggleButton>
                <ToggleButton value="engineers" sx={{ px: 2, textTransform: 'none', fontSize: '0.8rem' }}>
                  Для инженеров
                </ToggleButton>
              </ToggleButtonGroup>
            </Tooltip>
          )}
        </Box>

        {/* Форма */}
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Входные характеристики</Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            {/* Режим ввода — скрыт в компактном режиме (мощность и расход задаются пресетами) */}
            {!isCompactSquare && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>Режим ввода:</Typography>
                  <ToggleButtonGroup
                    value={characteristics.inputMode}
                    exclusive
                    onChange={(_, v) => { if (v) updateCharacteristic('inputMode', v); }}
                    size="small"
                  >
                    <ToggleButton value="power">По мощности</ToggleButton>
                    <ToggleButton value="flowRate">По расходу</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Grid>
            )}

            {/* Сечение + компактный (только для квадрата) */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
                <FormControl fullWidth sx={{ flex: '1 1 220px', minWidth: 180 }}>
                  <InputLabel>Сечение</InputLabel>
                  <Select
                    value={characteristics.shape}
                    label="Сечение"
                    onChange={(e) => updateCharacteristic('shape', e.target.value)}
                  >
                    <MenuItem value="round">Круглое</MenuItem>
                    <MenuItem value="square">Квадратное</MenuItem>
                  </Select>
                </FormControl>
                {characteristics.shape === 'square' && (
                  <FormControlLabel
                    sx={{ m: 0, flexShrink: 0 }}
                    control={
                      <Checkbox
                        checked={characteristics.useCompactDistribution}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setCharacteristics((p) => ({
                            ...p,
                            useCompactDistribution: on,
                            connectionSide: on ? 'none' : (p.connectionSide === 'none' ? 'left' : p.connectionSide),
                            ...(on
                              ? {
                                  power: 60,
                                  flowRate: 2.06,
                                  compactDistributionPresetKw: 60,
                                  ...(viewMode === 'engineers'
                                    ? { deltaTemp: CLIENT_THERMAL_CALC_DELTA_FALLBACK_KW }
                                    : {}),
                                }
                              : {}),
                          }));
                          if (on) setCommittedThermal({ power: 60, flowRate: 2.06 });
                        }}
                      />
                    }
                    label="Компактный"
                  />
                )}
              </Box>
            </Grid>

            {viewMode === 'engineers' && !isCompactSquare && (
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Разница температур, °C (ΔT)</InputLabel>
                  <Select
                    value={characteristics.deltaTemp}
                    label="Разница температур, °C (ΔT)"
                    onChange={(e) => updateCharacteristic('deltaTemp', Number(e.target.value))}
                  >
                    {CLIENT_THERMAL_DELTA_TEMP_CHOICES.map(v => (
                      <MenuItem key={v} value={v}>{v}°C</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Мощность / расход: в компактном режиме — список мощностей на месте поля мощности + поле расхода */}
            {isCompactSquare ? (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Тепловая мощность, кВт</InputLabel>
                    <Select
                      value={characteristics.compactDistributionPresetKw}
                      label="Тепловая мощность, кВт"
                      onChange={(e) => {
                        const kw = Number(e.target.value);
                        const row = COMPACT_DISTRIBUTION_COLLECTOR_PRESETS.find((p) => p.powerKw === kw);
                        if (!row) return;
                        setCharacteristics((p) => ({
                          ...p,
                          compactDistributionPresetKw: kw,
                          power: kw,
                          flowRate: row.flowM3h,
                        }));
                        setCommittedThermal({ power: kw, flowRate: row.flowM3h });
                      }}
                    >
                      {COMPACT_DISTRIBUTION_COLLECTOR_PRESETS.map((row) => (
                        <MenuItem key={row.powerKw} value={row.powerKw}>
                          {row.powerKw} кВт
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Расход теплоносителя, м³/ч"
                    type="number"
                    fullWidth
                    disabled
                    value={characteristics.flowRate === '' ? '' : characteristics.flowRate}
                    helperText="По выбранному варианту компактного коллектора"
                    InputProps={{ readOnly: true }}
                    sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0,0,0,0.87)' } }}
                  />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} sm={4}>
                  {characteristics.inputMode === 'power' ? (
                    <TextField
                      label="Тепловая мощность, кВт"
                      type="number"
                      fullWidth
                      value={characteristics.power === '' ? '' : characteristics.power}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '') { updateCharacteristic('power', ''); return; }
                        const n = parseFloat(v);
                        if (!isNaN(n)) updateCharacteristic('power', n);
                      }}
                      onBlur={(e) => {
                        const n = parseFloat(e.target.value);
                        const maxP = maxAvailablePowerKw;
                        if (viewMode === 'clients') {
                          const fallbackBase = Math.max(
                            COLLECTOR_CLIENT_POWER_STEPS[0],
                            Math.min(85, maxP)
                          );
                          const clampClientPowerBeforePreset = (raw) =>
                            clampCollectorClientPowerKw(raw, maxP);
                          if (isNaN(n) || n <= 0) {
                            const snapped = clampClientPowerBeforePreset(fallbackBase);
                            updateCharacteristic('power', snapped, { finalizeClientPreset: true });
                            setCommittedThermal((prev) => ({
                              ...(prev ?? {}),
                              power: snapped,
                            }));
                            return;
                          }
                          const next = clampClientPowerBeforePreset(n);
                          updateCharacteristic('power', next, { finalizeClientPreset: true });
                          setCommittedThermal((prev) => ({
                            ...(prev ?? {}),
                            power: next,
                          }));
                          return;
                        }
                        const fallback = Math.max(
                          THERMAL_POWER_INPUT_MIN_KW,
                          Math.min(85, maxP)
                        );
                        if (isNaN(n) || n <= 0) {
                          updateCharacteristic('power', fallback, { finalizeClientPreset: true });
                          setCommittedThermal((prev) => ({
                            ...(prev ?? {}),
                            power: fallback,
                          }));
                        } else {
                          const clamped = Math.min(
                            Math.max(n, THERMAL_POWER_INPUT_MIN_KW),
                            maxP
                          );
                          updateCharacteristic('power', clamped, { finalizeClientPreset: true });
                          setCommittedThermal((prev) => ({
                            ...(prev ?? {}),
                            power: clamped,
                          }));
                        }
                      }}
                      inputProps={{
                        min: THERMAL_POWER_INPUT_MIN_KW,
                        max: maxAvailablePowerKw,
                        step: 1,
                      }}
                      helperText={
                        !hasValidDiameter
                          ? 'Нет подходящих сечений коллектора (0.2–0.8 м/с) — измените ΔT'
                          : (needsEndConnection && !hasValidConnectionSelection
                            ? 'Нет размера торцевого фитинга в диапазоне 0.3–1.5 м/с — измените мощность/расход, ΔT или категорию'
                            : `От ${THERMAL_POWER_INPUT_MIN_KW} до ${maxAvailablePowerKw} кВт`)
                      }
                      error={!hasValidDiameter || (needsEndConnection && !hasValidConnectionSelection)}
                    />
                  ) : (
                    <TextField
                      label="Расход теплоносителя, м³/ч"
                      type="number"
                      fullWidth
                      value={characteristics.flowRate === '' ? '' : characteristics.flowRate}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '') { updateCharacteristic('flowRate', ''); return; }
                        const n = parseFloat(v);
                        if (!isNaN(n)) updateCharacteristic('flowRate', n);
                      }}
                      onBlur={(e) => {
                        const n = parseFloat(e.target.value);
                        const maxQ = maxFlowRateForPowerLimit;
                        if (isNaN(n) || n <= 0) {
                          const fb = Math.min(1, maxQ);
                          updateCharacteristic('flowRate', parseFloat(fb.toFixed(2)));
                          const q = parseFloat(fb.toFixed(2));
                          setCommittedThermal((prev) => ({
                            ...(prev ?? {}),
                            flowRate: q,
                          }));
                          return;
                        }
                        const clamped = Math.min(Math.max(n, 0.1), maxQ);
                        const qFinal = parseFloat(clamped.toFixed(2));
                        updateCharacteristic('flowRate', qFinal);
                        setCommittedThermal((prev) => ({
                          ...(prev ?? {}),
                          flowRate: qFinal,
                        }));
                      }}
                      inputProps={{
                        min: 0.1,
                        max: Number.isFinite(maxFlowRateForPowerLimit) ? maxFlowRateForPowerLimit : undefined,
                        step: 0.1,
                      }}
                      helperText={
                        !hasValidDiameter
                          ? 'Нет подходящих сечений коллектора (0.2–0.8 м/с) — измените ΔT'
                          : (needsEndConnection && !hasValidConnectionSelection
                            ? 'Нет размера торцевого фитинга в диапазоне 0.3–1.5 м/с — измените мощность/расход, ΔT или категорию'
                            : `Расход ограничен мощностью до ${maxAvailablePowerKw} кВт при ΔT = ${effectiveThermalDeltaTemp}°C`)
                      }
                      error={!hasValidDiameter || (needsEndConnection && !hasValidConnectionSelection)}
                    />
                  )}
                </Grid>

                <Grid item xs={12} sm={4}>
                  {characteristics.inputMode === 'power' ? (
                    <TextField
                      label="Рассчитанный расход, м³/ч"
                      value={displayFlowRate}
                      fullWidth disabled
                      InputProps={{ readOnly: true }}
                      helperText=" "
                      FormHelperTextProps={{ sx: { m: 0, minHeight: '1.25rem' } }}
                      sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0,0,0,0.87)' } }}
                    />
                  ) : (
                    <TextField
                      label="Рассчитанная мощность, кВт"
                      value={displayPower}
                      fullWidth disabled
                      InputProps={{ readOnly: true }}
                      helperText=" "
                      FormHelperTextProps={{ sx: { m: 0, minHeight: '1.25rem' } }}
                      sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0,0,0,0.87)' } }}
                    />
                  )}
                </Grid>

                {viewMode === 'clients' && !isCompactSquare && (
                  <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <FormControl fullWidth>
                      <InputLabel>Разница температур, °C (ΔT)</InputLabel>
                      <Select
                        value={normalizeClientThermalDeltaTemp(characteristics.deltaTemp)}
                        label="Разница температур, °C (ΔT)"
                        disabled={clientPresetDeltaTempOn}
                        onChange={(e) => updateCharacteristic('deltaTemp', Number(e.target.value))}
                      >
                        {CLIENT_THERMAL_DELTA_TEMP_CHOICES.map((v) => (
                          <MenuItem key={v} value={v}>{v}°C</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText component="div" sx={{ mx: 0, mt: 0.5, minHeight: '1.25rem' }}>
                        <FormControlLabel
                          sx={{ m: 0, alignItems: 'center' }}
                          control={
                            <Checkbox
                              size="small"
                              checked={clientPresetDeltaTempOn}
                              onChange={(e) =>
                                updateCharacteristic('clientPresetDeltaTemp', e.target.checked)
                              }
                            />
                          }
                          label={CLIENT_STANDARD_PRODUCTS_DELTA_TEMP_LABEL}
                        />
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                )}
              </>
            )}

            {/* Давление и заглушка — инженеры; в компактном квадрате скрыто */}
            {viewMode === 'engineers' && !isCompactSquare && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Давление</InputLabel>
                    <Select
                      value={characteristics.pressure}
                      label="Давление"
                      onChange={(e) => updateCharacteristic('pressure', Number(e.target.value))}
                    >
                      <MenuItem
                        value={4}
                        sx={{ display: characteristics.shape === 'round' ? 'none' : 'block' }}
                      >
                        4 бар рабочее / 6 бар испытательное
                      </MenuItem>
                      <MenuItem
                        value={6}
                        disabled={
                          characteristics.shape === 'square' ||
                          (characteristics.shape === 'round' &&
                            (roundPipeNoSixEightPressure || bothSidesNeedConcentricReducer))
                        }
                      >
                        6 бар рабочее / 8 бар испытательное
                        {characteristics.shape === 'square' && ' (только для круглого)'}
                        {characteristics.shape === 'round' &&
                          bothSidesNeedConcentricReducer &&
                          ' (оба торца — конц. переходы)'}
                        {characteristics.shape === 'round' &&
                          !bothSidesNeedConcentricReducer &&
                          selectedDiameter != null &&
                          selectedDiameter > 60.3 &&
                          ' (только ⌀ коллектора ≤ 60.3)'}
                      </MenuItem>
                      <MenuItem value={8} disabled={characteristics.shape === 'square'}>
                        8 бар рабочее / 10 бар испытательное
                        {characteristics.shape === 'square' && ' (только для круглого)'}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Заглушка"
                    value={
                      bothSidesNeedConcentricReducer
                        ? 'Концентрические переходы (8/10 бар)'
                        : capDisplayLabel(characteristics.shape, characteristics.pressure, !isSquare ? selectedDiameter : null)
                    }
                    fullWidth
                    disabled
                    InputProps={{ readOnly: true }}
                    sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0,0,0,0.87)' } }}
                  />
                </Grid>
              </>
            )}

            {/* Диаметр / профиль коллектора — в компактном квадрате только текст, как у клиентов */}
            <Grid item xs={12} sm={!isCompactSquare && !(viewMode === 'clients' && clientPresetCollectorSizeOn) ? 6 : 12}>
              {isCompactSquare ? (
                <TextField
                  label={isSquare ? 'Профиль коллектора' : 'Диаметр коллектора'}
                  value={clientCollectorSizeLabel}
                  fullWidth
                  disabled
                  InputProps={{ readOnly: true }}
                  helperText="Компактный коллектор: профиль по выбранной мощности"
                  sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0,0,0,0.87)' } }}
                />
              ) : viewMode === 'clients' && clientPresetCollectorSizeOn ? (
                <FormControl fullWidth>
                  <TextField
                    label={isSquare ? 'Профиль коллектора' : 'Диаметр коллектора'}
                    value={clientCollectorSizeLabel}
                    fullWidth
                    disabled
                    InputProps={{ readOnly: true }}
                    sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0,0,0,0.87)' } }}
                  />
                  <FormHelperText component="div" sx={{ mx: 0, mt: 0.5, minHeight: '1.25rem' }}>
                    <FormControlLabel
                      sx={{ m: 0, alignItems: 'center' }}
                      control={
                        <Checkbox
                          size="small"
                          checked={clientPresetCollectorSizeOn}
                          onChange={(e) =>
                            updateCharacteristic('clientPresetCollectorSize', e.target.checked)
                          }
                        />
                      }
                      label={CLIENT_STANDARD_PRODUCTS_CONNECTION_LABEL}
                    />
                  </FormHelperText>
                </FormControl>
              ) : (
                <FormControl fullWidth>
                  <InputLabel>
                    {isSquare ? 'Профиль коллектора' : 'Диаметр коллектора'}
                  </InputLabel>
                  {!isSquare ? (
                    <Select
                      value={collectorDiameterSelectValue}
                      label="Диаметр коллектора"
                      onChange={(e) => updateCharacteristic('collectorDiameter', Number(e.target.value))}
                    >
                      {filteredDiameters.map(({ value }) => {
                        const wt = collectorCharacteristicsRoundWallThickness(value);
                        const inner = value - 2 * wt;
                        return (
                          <MenuItem key={value} value={value}>
                            ⌀{value} (внутр. ⌀{inner})
                          </MenuItem>
                        );
                      })}
                    </Select>
                  ) : (
                    <Select
                      value={collectorProfileSelectValue}
                      label="Профиль коллектора"
                      disabled={characteristics.useCompactDistribution}
                      onChange={(e) => updateCharacteristic('collectorProfile', e.target.value)}
                    >
                      {filteredProfiles.map(({ value, label, side }) => {
                        const wt = collectorCharacteristicsSquareWallThickness(side);
                        const inner = side - 2 * wt;
                        return (
                          <MenuItem key={value} value={value}>
                            {label} (внутр. {inner}×{inner})
                          </MenuItem>
                        );
                      })}
                    </Select>
                  )}
                  {viewMode === 'clients' && (
                    <FormHelperText component="div" sx={{ mx: 0, mt: 0.5, minHeight: '1.25rem' }}>
                      <FormControlLabel
                        sx={{ m: 0, alignItems: 'center' }}
                        control={
                          <Checkbox
                            size="small"
                            checked={clientPresetCollectorSizeOn}
                            onChange={(e) =>
                              updateCharacteristic('clientPresetCollectorSize', e.target.checked)
                            }
                          />
                        }
                        label={CLIENT_STANDARD_PRODUCTS_CONNECTION_LABEL}
                      />
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            </Grid>

            {/* Скорость в коллекторе — инженеры и клиент при ручном ⌀ */}
            {(viewMode === 'engineers' || (viewMode === 'clients' && !clientPresetCollectorSizeOn)) && !isCompactSquare && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Скорость в коллекторе, м/с"
                  value={currentVelocity != null ? currentVelocity.toFixed(2) : '—'}
                  fullWidth disabled
                  InputProps={{ readOnly: true }}
                  helperText="В корпусе гребёнки (0.2–0.8 м/с)"
                  sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0,0,0,0.87)' } }}
                />
              </Grid>
            )}

            {/* Торцевое подключение — не используется в компактном режиме */}
            {!isCompactSquare && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, borderColor: 'divider' }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}
                >
                  Подключение источника
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={needsEndConnection ? 4 : 12}>
                    <FormControl fullWidth>
                      <InputLabel>Торцевое подключение</InputLabel>
                      <Select
                        value={characteristics.connectionSide}
                        label="Торцевое подключение"
                        onChange={(e) => updateCharacteristic('connectionSide', e.target.value)}
                      >
                        <MenuItem value="left">Слева</MenuItem>
                        <MenuItem value="right">Справа</MenuItem>
                        <MenuItem value="both">С обеих сторон</MenuItem>
                        <MenuItem value="none">Нет подключений</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {needsEndConnection && (viewMode === 'engineers' || viewMode === 'clients') && (
                    <>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                          <InputLabel>Категория</InputLabel>
                          <Select
                            value={characteristics.endConnectionCategory}
                            label="Категория"
                            disabled={clientCatalogConnectionPresetOn}
                            onChange={(e) => updateCharacteristic('endConnectionCategory', e.target.value)}
                          >
                            {endConnectionCategoryMenuItems.map((id) => (
                              <MenuItem key={id} value={id}>{id}</MenuItem>
                            ))}
                          </Select>
                          {viewMode === 'clients' && (
                            <FormHelperText component="div" sx={{ mx: 0, mt: 0.5, minHeight: '1.25rem' }}>
                              <FormControlLabel
                                sx={{ m: 0, alignItems: 'center' }}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={clientPresetConnectionTypeOn}
                                    onChange={(e) =>
                                      updateCharacteristic('clientPresetConnectionType', e.target.checked)
                                    }
                                  />
                                }
                                label={CLIENT_STANDARD_PRODUCTS_CONNECTION_LABEL}
                              />
                            </FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        {clientCatalogConnectionPresetOn ? (
                          <TextField
                            label="Размер"
                            value={clientLockedEndConnectionSizeLabel}
                            fullWidth
                            disabled
                            InputProps={{ readOnly: true }}
                            helperText="Из строки каталога (по расходу / конструкции)"
                            error={needsEndConnection && !hasValidConnectionSelection}
                            sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0,0,0,0.87)' } }}
                          />
                        ) : (
                          <FormControl fullWidth error={needsEndConnection && !hasValidConnectionSelection}>
                            <InputLabel>Размер</InputLabel>
                            <Select
                              value={endConnectionSizeSelectValue}
                              label="Размер"
                              onChange={(e) => updateCharacteristic('endConnectionSize', parseFloat(e.target.value))}
                            >
                              {filteredEndConnectionSizes.map((s) => {
                                const v = velocityForConnectionSize(endConnectionType, s.diameter, actualFlowRate);
                                const velSuffix = v != null ? ` (${v.toFixed(2)} м/с)` : '';
                                return (
                                  <MenuItem key={s.diameter} value={s.diameter}>
                                    {s.displayName}{velSuffix}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                            <FormHelperText>
                              {connectionVelocityDisplay != null
                                ? `Скорость в подключении: ${connectionVelocityDisplay.toFixed(2)} м/с (0.3–1.5)`
                                : 'Нет размеров в диапазоне 0.3–1.5 м/с для выбранной категории и гребёнки'}
                            </FormHelperText>
                          </FormControl>
                        )}
                      </Grid>
                    </>
                  )}
                </Grid>
              </Paper>
            </Grid>
            )}
          </Grid>
        </Paper>

        {/* Контуры */}
        {(() => {
          const connSide     = characteristics.connectionSide;
          const showLeftEnd  = connSide !== 'left'  && connSide !== 'both';
          const showRightEnd = connSide !== 'right' && connSide !== 'both';
          const hasAnyEnd    = showLeftEnd || showRightEnd;
          const LABEL_COL    = 60;

          const ROW_COLOR  = { supply: '#1976d2', return: '#d32f2f', leftEnd: '#7b1fa2', rightEnd: '#7b1fa2' };
          const END_COLOR  = '#7b1fa2';

          // рендер одного чипа контура — обычная функция, не компонент
          const renderChip = (circuit, index, row, onRemove) => {
            const fitFull   = fittingsData[circuit.fitting]?.displayName ?? circuit.fitting;
            const catLabel  = CIRCUIT_FITTING_CATEGORIES.find(c => c.id === circuit.fittingCategory)?.label ?? circuit.fittingCategory;
            const chipLabel = index != null ? `${index + 1}. ${fitFull}` : fitFull;
            const chipColor = ROW_COLOR[row] ?? '#555';
            return (
              <Tooltip key={circuit.id ?? `${row}-${index}`} title={`${catLabel} / ${fitFull} — нажмите для изменения`}>
                <Chip
                  label={chipLabel}
                  size="small"
                  onClick={(e) => openCircuitPopover(e, row, index)}
                  onDelete={onRemove}
                  deleteIcon={<DeleteIcon sx={{ fontSize: '0.8rem !important' }} />}
                  sx={{
                    cursor: 'pointer',
                    height: 24,
                    bgcolor: `${chipColor}18`,
                    border: `1.5px solid ${chipColor}`,
                    color: chipColor,
                    fontWeight: 600,
                    '& .MuiChip-label': { fontSize: '0.7rem', px: 0.75 },
                    '& .MuiChip-deleteIcon': { ml: 0.25, mr: 0.5, color: chipColor, opacity: 0.7, '&:hover': { opacity: 1 } },
                  }}
                />
              </Tooltip>
            );
          };

          // рендер торцевой кнопки/чипа
          const renderEndSlot = (side) => {
            const endCircuit = side === 'left' ? leftEndCircuit : rightEndCircuit;
            const setEnd     = side === 'left' ? setLeftEndCircuit : setRightEndCircuit;
            const endRow     = side === 'left' ? 'leftEnd' : 'rightEnd';
            const arrow      = side === 'left' ? '←' : '→';
            const sideLabel  = side === 'left' ? 'Левый торец' : 'Правый торец';
            return (
              <Box key={side} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                <Typography variant="caption" sx={{ fontSize: '0.6rem', color: END_COLOR, fontWeight: 700, lineHeight: 1 }}>
                  {sideLabel}
                </Typography>
                {endCircuit
                  ? renderChip(endCircuit, null, endRow, () => setEnd(null))
                  : (
                    <Tooltip title={`Добавить торцевой контур (${side === 'left' ? 'слева' : 'справа'})`}>
                      <IconButton size="small"
                        onClick={(e) => {
                          const el = e.currentTarget;
                          const rect = el?.getBoundingClientRect();
                          const anchorPosition = rect
                            ? { top: rect.bottom + window.scrollY, left: rect.left + window.scrollX }
                            : { top: 0, left: 0 };
                          setEnd({ fittingCategory: DEFAULT_FITTING_CATEGORY, fitting: DEFAULT_FITTING_ID });
                          setCircuitPopover({ anchorPosition, row: endRow, index: null, step: 'category', category: DEFAULT_FITTING_CATEGORY });
                        }}
                        sx={{
                          width: 28, height: 28,
                          bgcolor: `${END_COLOR}18`,
                          border: `1.5px dashed ${END_COLOR}`,
                          color: END_COLOR,
                          borderRadius: '6px',
                          '&:hover': { bgcolor: `${END_COLOR}30`, borderStyle: 'solid' },
                        }}>
                        <Typography variant="caption" sx={{ fontSize: '0.85rem', lineHeight: 1, fontWeight: 700 }}>{arrow}</Typography>
                      </IconButton>
                    </Tooltip>
                  )
                }
              </Box>
            );
          };

          return (
            <Paper elevation={3} sx={{ mb: 3, overflow: 'hidden' }}>
              <Box sx={{ px: 3, pt: 2, pb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Контуры</Typography>
              </Box>
              <Divider />

              {/* ── Подача ── */}
              {[
                { row: 'supply', label: 'Подача',  circuits: supplyCircuits, color: '#1976d2', bg: 'rgba(25,118,210,0.03)' },
                { row: 'return', label: 'Обратка', circuits: returnCircuits, color: '#d32f2f', bg: 'rgba(211,47,47,0.03)' },
              ].map(({ row, label, circuits, color, bg }, rowIdx) => (
                <Box key={row}>
                  {rowIdx === 1 && !isCompactSquare && (
                    /* ── Разделитель с торцевыми кнопками (скрыт в компактном режиме) ── */
                    <Box sx={{
                      display: 'flex', alignItems: 'center',
                      px: 2.5, py: 0.75, bgcolor: 'grey.50',
                      borderTop: '1px dashed', borderBottom: '1px dashed', borderColor: 'divider',
                    }}>
                      <Box sx={{ width: LABEL_COL + 8 + 32, flexShrink: 0 }} />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                        {showLeftEnd  && renderEndSlot('left')}
                        {hasAnyEnd && (
                          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.63rem', fontStyle: 'italic', mx: 'auto' }}>
                            торцевые контуры (необязательно)
                          </Typography>
                        )}
                        {showRightEnd && renderEndSlot('right')}
                      </Box>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.25, px: 2.5, bgcolor: bg }}>
                    {/* Метка */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, width: LABEL_COL, flexShrink: 0 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
                      <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', color, lineHeight: 1 }}>
                        {label}
                      </Typography>
                    </Box>
                    {/* Кнопка + */}
                    <Tooltip title="Добавить контур">
                      <IconButton size="small" onClick={() => addCircuit(row)}
                        sx={{ bgcolor: color, color: 'white', width: 22, height: 22, flexShrink: 0, '&:hover': { opacity: 0.85 } }}>
                        <AddIcon sx={{ fontSize: '0.85rem' }} />
                      </IconButton>
                    </Tooltip>
                    {/* Чипы — ml:4 выравнивание с торцевым рядом; в компактном режиме торцевых нет */}
                    <Box sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.5,
                      flex: 1,
                      ml: isCompactSquare ? 0 : 4,
                    }}>
                      {circuits.map((circuit, i) =>
                        renderChip(circuit, i, row, () => removeCircuit(row, i))
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontSize: '0.7rem' }}>
                      {circuits.length} шт.
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Paper>
          );
        })()}

        {/* Popover: выбор категории → фитинга */}
        {(() => {
          const pop = circuitPopover;
          const popCur = pop
            ? (pop.row === 'supply'   ? supplyCircuits[pop.index]
             : pop.row === 'return'   ? returnCircuits[pop.index]
             : pop.row === 'leftEnd'  ? leftEndCircuit
             : pop.row === 'rightEnd' ? rightEndCircuit
             : null)
            : null;

          // Определяем функцию проверки совместимости для текущего контура
          // selectedDiameter / selectedProfile — актуальный диаметр/профиль гребёнки (из useMemo выше)
          let compatibilityFn = null;
          if (pop) {
            if (pop.row === 'supply' || pop.row === 'return') {
              compatibilityFn = isSquare
                ? (squareProfileForCircuitFittingRules
                    ? (id) =>
                        isDistributionCircuitFittingCompatibleWithSquareProfile(
                          id,
                          squareProfileForCircuitFittingRules
                        )
                    : null)
                : selectedDiameter
                  ? (id) => isCircuitFittingCompatible(id, selectedDiameter)
                  : null;
            } else if (pop.row === 'leftEnd' || pop.row === 'rightEnd') {
              compatibilityFn = isSquare
                ? squareProfileForCircuitFittingRules
                  ? (id) => isEndFittingCompatibleWithSquareProfile(id, squareProfileForCircuitFittingRules)
                  : null
                : selectedDiameter ? (id) => isEndFittingCompatibleWithMainPipe(id, selectedDiameter) : null;
            }
          }

          return (
            <Popover
              open={Boolean(pop)}
              anchorReference="anchorPosition"
              anchorPosition={pop?.anchorPosition ?? { top: 0, left: 0 }}
              onClose={closeCircuitPopover}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{ sx: { mt: 0.5, minWidth: 220, maxHeight: 320 } }}
            >
              {pop?.step === 'category' ? (
                <>
                  <Typography variant="caption" sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'block', color: 'text.secondary', fontWeight: 600 }}>
                    Категория фитинга
                  </Typography>
                  <List dense disablePadding>
                    {CIRCUIT_FITTING_CATEGORIES.map(({ id, label }) => (
                      <ListItem key={id} disablePadding>
                        <ListItemButton selected={popCur?.fittingCategory === id} onClick={() => selectCategory(id)} sx={{ px: 2, py: 0.6 }}>
                          <ListItemText primary={label} primaryTypographyProps={{ variant: 'body2' }} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </>
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', px: 1, pt: 1 }}>
                    <IconButton size="small" onClick={() => setCircuitPopover(prev => prev ? { ...prev, step: 'category' } : prev)}>
                      <ArrowBackIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                      {CIRCUIT_FITTING_CATEGORIES.find(c => c.id === pop?.category)?.label}
                    </Typography>
                  </Box>
                  <List dense disablePadding sx={{ overflowY: 'auto', maxHeight: 240 }}>
                    {getFittingsByCategory(pop?.category ?? DEFAULT_FITTING_CATEGORY, compatibilityFn).map(({ id, label, shortLabel, isCompatible }) => (
                      <ListItem key={id} disablePadding>
                        <ListItemButton
                          selected={popCur?.fitting === id}
                          onClick={() => isCompatible !== false && selectFitting(id)}
                          disabled={isCompatible === false}
                          sx={{ px: 2, py: 0.4, opacity: isCompatible === false ? 0.4 : 1 }}
                        >
                          <ListItemText
                            primary={shortLabel}
                            secondary={isCompatible === false ? `${label} — увеличьте Ø коллектора` : label}
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                            secondaryTypographyProps={{ variant: 'caption', color: isCompatible === false ? 'error' : 'text.secondary' }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </Popover>
          );
        })()}

        {/* Кнопка */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<PlayArrowIcon />}
            onClick={calculateParameters}
            disabled={!hasValidDiameter || !hasValidConnectionSelection}
            sx={{ py: 1.5, px: 4, fontSize: '1.1rem', fontWeight: 600 }}
          >
            Рассчитать параметры
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

export default CollectorCharacteristicsCalculator;
